import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  LinearProgress,
  MenuItem,
  Radio,
  RadioGroup,
  FormControlLabel,
  TextField,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import BuildOutlinedIcon from "@mui/icons-material/BuildOutlined";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import ForumOutlinedIcon from "@mui/icons-material/ForumOutlined";
import LinkOutlinedIcon from "@mui/icons-material/LinkOutlined";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import AgentToolLoadout from "./AgentToolLoadout";
import TuneIcon from "@mui/icons-material/Tune";
import Dropzone from "react-dropzone";
import TipTapEditor from "../TipTap/TipTapEditor";
import useAgentStore from "./useAgentStore";
import useAgentKnowledgeStore from "./useAgentKnowledgeStore";
import usePublicChatStore from "./usePublicChatStore";
import { send_request } from "../../utils/Request";
import { DEFAULT_MODEL } from "../../utils/ModelConfig";
import ModelSelectField from "../common/ModelSelectField";
import PeoplePicker from "../common/PeoplePicker";
import ToggleRow from "../common/ToggleRow";
import PublicAccessPolicyEditor from "../access/PublicAccessPolicyEditor";
import "../Organization/OrganizationSettings.css";
import config from "../../config";
import { ICON_COMPONENT_MAP } from "./agentToolIcons";
import AgentConfigDrawerShell from "./agentConfig/AgentConfigDrawerShell";
import {
  AgentConfigAiCreationPanel,
  AgentConfigCreationPathChooser,
} from "./agentConfig/AgentConfigCreationPanels";
import { buildAgentConfigChromeState } from "./agentConfig/useAgentConfigDraft";

const NAV_TABS = [
  { id: "general", label: "General", icon: TuneIcon },
  { id: "access", label: "Access", icon: VisibilityOutlinedIcon },
  { id: "chatUx", label: "Chat UX", icon: ForumOutlinedIcon },
  { id: "tools", label: "Tools", icon: BuildOutlinedIcon },
  { id: "knowledge", label: "Knowledge", icon: MenuBookOutlinedIcon },
  { id: "email", label: "Email", icon: EmailOutlinedIcon },
  { id: "danger", label: "Delete", icon: DeleteOutlineIcon, danger: true },
];

const ACCEPTED_KNOWLEDGE_FORMATS =
  ".csv,.json,.pdf,.xlsx,.xls,.txt,.md,.docx,.pptx";
const MAX_KNOWLEDGE_FILES = 5;
const ResolvedPeoplePicker =
  typeof PeoplePicker === "function" ? PeoplePicker : null;
export const SELF_CONFIGURATION_MODE_OFF = "OFF";
export const SELF_CONFIGURATION_MODE_AWARENESS_ONLY = "AWARENESS_ONLY";
export const SELF_CONFIGURATION_MODE_AWARENESS_AND_EDIT = "AWARENESS_AND_EDIT";
const SELF_CONFIG_AWARENESS_TOOL_NAME = "get_self_config";
const SELF_CONFIG_EDIT_TOOL_NAME = "update_self_config";
const SELF_CONFIGURATION_TOOL_NAMES = new Set([
  SELF_CONFIG_AWARENESS_TOOL_NAME,
  SELF_CONFIG_EDIT_TOOL_NAME,
]);
const TASK_QUEUE_PLAN_TOOL_NAME = "plan_tasks";
const TASK_QUEUE_UPDATE_TOOL_NAME = "update_task_queue";
const SCHEDULE_MESSAGE_TOOL_NAME = "schedule_message";
const EDIT_SCHEDULED_MESSAGE_TOOL_NAME = "edit_scheduled_message";
const RECURRING_SCHEDULE_TOOL_NAME = "recurring_schedule";
const EDIT_RECURRING_SCHEDULE_TOOL_NAME = "edit_recurring_schedule";
const CREATE_CALENDAR_EVENT_TOOL_NAME = "create_calendar_event";
const EDIT_CALENDAR_EVENT_TOOL_NAME = "edit_calendar_event";
const RUN_WORKFLOW_TOOL_NAME = "run_workflow";
const READ_WORKFLOW_TOOL_NAME = "read_workflow";
const READ_COMPONENT_TOOL_NAME = "read_component";
const PAIRED_TOOL_DEPENDENCIES = [
  {
    parentToolName: TASK_QUEUE_PLAN_TOOL_NAME,
    childToolName: TASK_QUEUE_UPDATE_TOOL_NAME,
  },
  {
    parentToolName: SCHEDULE_MESSAGE_TOOL_NAME,
    childToolName: EDIT_SCHEDULED_MESSAGE_TOOL_NAME,
  },
  {
    parentToolName: RECURRING_SCHEDULE_TOOL_NAME,
    childToolName: EDIT_RECURRING_SCHEDULE_TOOL_NAME,
  },
  {
    parentToolName: CREATE_CALENDAR_EVENT_TOOL_NAME,
    childToolName: EDIT_CALENDAR_EVENT_TOOL_NAME,
  },
  {
    parentToolName: RUN_WORKFLOW_TOOL_NAME,
    childToolName: READ_WORKFLOW_TOOL_NAME,
  },
  {
    parentToolName: RUN_WORKFLOW_TOOL_NAME,
    childToolName: READ_COMPONENT_TOOL_NAME,
  },
];
/**
 * Tools enabled by default for every new agent.
 * Users can manually turn these off in the config UI.
 */
const DEFAULT_ENABLED_TOOLS = [
  "read_file",
  "schedule_message",
  "edit_scheduled_message",
  "send_email",
  "web_search",
  "extract_webpage",
  "fill_form",
  "plan_tasks",
  "update_task_queue",
  "review_task",
  "get_form",
  "form_fields",
];
const TOOL_LABEL_OVERRIDES = {
  form_fields: "Generate Form Fields",
};
export const PAIRED_PARENT_TOOL_LABELS = {
  [TASK_QUEUE_PLAN_TOOL_NAME]: "Plan Tasks",
  [SCHEDULE_MESSAGE_TOOL_NAME]: "Schedule Message",
  [RECURRING_SCHEDULE_TOOL_NAME]: "Recurring Schedule",
  [CREATE_CALENDAR_EVENT_TOOL_NAME]: "Create Calendar Event",
  [RUN_WORKFLOW_TOOL_NAME]: "Run Workflow",
};

export const TOOL_ICON_MAP = {
  web_search: ICON_COMPONENT_MAP.SearchIcon,
  http_request: ICON_COMPONENT_MAP.HttpIcon,
  extract_webpage: ICON_COMPONENT_MAP.LanguageIcon,
  send_email: ICON_COMPONENT_MAP.EmailOutlinedIcon,
  list_agents: ICON_COMPONENT_MAP.SmartToyOutlinedIcon,
  list_agent_sessions: ICON_COMPONENT_MAP.ForumOutlinedIcon,
  send_message: ICON_COMPONENT_MAP.ForumOutlinedIcon,
  get_self_config: ICON_COMPONENT_MAP.SettingsSuggestOutlinedIcon,
  update_self_config: ICON_COMPONENT_MAP.EditNoteOutlinedIcon,
  create_agent: ICON_COMPONENT_MAP.SmartToyOutlinedIcon,
  run_workflow: ICON_COMPONENT_MAP.AccountTreeOutlinedIcon,
  get_session: ICON_COMPONENT_MAP.HistoryOutlinedIcon,
  create_table: ICON_COMPONENT_MAP.TableChartOutlinedIcon,
  add_column: ICON_COMPONENT_MAP.ViewColumnOutlinedIcon,
  rename_column: ICON_COMPONENT_MAP.DriveFileRenameOutlineIcon,
  delete_column: ICON_COMPONENT_MAP.DeleteOutlineIcon,
  create_row: ICON_COMPONENT_MAP.PlaylistAddOutlinedIcon,
  update_row: ICON_COMPONENT_MAP.EditOutlinedIcon,
  query_table: ICON_COMPONENT_MAP.StorageOutlinedIcon,
  delete_row: ICON_COMPONENT_MAP.DeleteOutlineIcon,
  form_fields: ICON_COMPONENT_MAP.DynamicFormOutlinedIcon,
  get_form: ICON_COMPONENT_MAP.AssignmentOutlinedIcon,
  fill_form: ICON_COMPONENT_MAP.FactCheckOutlinedIcon,
  review_task: ICON_COMPONENT_MAP.RateReviewOutlinedIcon,
  plan_tasks: ICON_COMPONENT_MAP.ListAltOutlinedIcon,
  update_task_queue: ICON_COMPONENT_MAP.UpdateOutlinedIcon,
  run_code: ICON_COMPONENT_MAP.CodeOutlinedIcon,
  generate_document: ICON_COMPONENT_MAP.DescriptionOutlinedIcon,
  send_slack_component: ICON_COMPONENT_MAP.SendOutlinedIcon,
  schedule_message: ICON_COMPONENT_MAP.ScheduleSendOutlinedIcon,
  edit_scheduled_message: ICON_COMPONENT_MAP.EditCalendarOutlinedIcon,
  recurring_schedule: ICON_COMPONENT_MAP.EventRepeatOutlinedIcon,
  edit_recurring_schedule: ICON_COMPONENT_MAP.EditCalendarOutlinedIcon,
  create_calendar_event: ICON_COMPONENT_MAP.CalendarTodayOutlinedIcon,
  read_calendar_event: ICON_COMPONENT_MAP.EventNoteOutlinedIcon,
  edit_calendar_event: ICON_COMPONENT_MAP.EditCalendarIcon,
};

export const CATEGORY_COLORS = {
  General: "#4285f4",
  Agents: "#34a853",
  Workflows: "#9c27b0",
  Tables: "#f9a825",
  "Tasks & Forms": "#ea4335",
  Actions: "#00897b",
  Calendar: "#e67c11",
  Files: "#0097a7",
  Workers: "#5e35b1",
  Sessions: "#3949ab",
  Components: "#d81b60",
  "Workflow Builder": "#00838f",
};
export const CATEGORY_COLOR_FALLBACK = "#4285f4";

export function normalizeSelfConfigurationMode(mode) {
  const normalized = String(mode || SELF_CONFIGURATION_MODE_OFF).toUpperCase();
  if (
    normalized === SELF_CONFIGURATION_MODE_AWARENESS_ONLY ||
    normalized === SELF_CONFIGURATION_MODE_AWARENESS_AND_EDIT
  ) {
    return normalized;
  }
  return SELF_CONFIGURATION_MODE_OFF;
}

export function isSelfConfigurationAwarenessEnabled(mode) {
  return normalizeSelfConfigurationMode(mode) !== SELF_CONFIGURATION_MODE_OFF;
}

export function isSelfConfigurationEditEnabled(mode) {
  return (
    normalizeSelfConfigurationMode(mode) ===
    SELF_CONFIGURATION_MODE_AWARENESS_AND_EDIT
  );
}

export function formatToolNameAsLabel(toolName) {
  return String(toolName || "")
    .split("_")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

export function getSelfConfigurationModeHelpText(mode) {
  const normalized = normalizeSelfConfigurationMode(mode);
  if (normalized === SELF_CONFIGURATION_MODE_OFF) {
    return "Awareness OFF: no self-config tools are exposed. Changes must be made manually in settings.";
  }
  if (normalized === SELF_CONFIGURATION_MODE_AWARENESS_ONLY) {
    return "Awareness ON, Edit OFF: the agent can inspect configuration and recommend manual changes.";
  }
  return "Awareness ON, Edit ON: the agent can inspect and update its configuration. Edits apply to subsequent runs/messages.";
}

export function deriveSelfConfigurationModeFromEnabledTools(enabledTools) {
  const dedupedTools = Array.isArray(enabledTools)
    ? [
        ...new Set(
          enabledTools.filter(
            (toolName) => typeof toolName === "string" && toolName.trim()
          )
        ),
      ]
    : [];
  if (dedupedTools.includes(SELF_CONFIG_EDIT_TOOL_NAME)) {
    return SELF_CONFIGURATION_MODE_AWARENESS_AND_EDIT;
  }
  if (dedupedTools.includes(SELF_CONFIG_AWARENESS_TOOL_NAME)) {
    return SELF_CONFIGURATION_MODE_AWARENESS_ONLY;
  }
  return SELF_CONFIGURATION_MODE_OFF;
}

export function ensureSelfConfigurationToolsForMode(enabledTools, mode) {
  const normalizedMode = normalizeSelfConfigurationMode(mode);
  const dedupedTools = Array.isArray(enabledTools)
    ? [
        ...new Set(
          enabledTools.filter(
            (toolName) => typeof toolName === "string" && toolName.trim()
          )
        ),
      ]
    : [];
  const toolsWithoutSelfConfig = dedupedTools.filter(
    (toolName) => !SELF_CONFIGURATION_TOOL_NAMES.has(toolName)
  );

  if (normalizedMode === SELF_CONFIGURATION_MODE_OFF) {
    return toolsWithoutSelfConfig;
  }
  if (normalizedMode === SELF_CONFIGURATION_MODE_AWARENESS_ONLY) {
    return [...toolsWithoutSelfConfig, SELF_CONFIG_AWARENESS_TOOL_NAME];
  }
  return [
    ...toolsWithoutSelfConfig,
    SELF_CONFIG_AWARENESS_TOOL_NAME,
    SELF_CONFIG_EDIT_TOOL_NAME,
  ];
}

export function ensureTaskQueuePlannerToolsPaired(enabledTools) {
  const dedupedTools = Array.isArray(enabledTools)
    ? [
        ...new Set(
          enabledTools.filter(
            (toolName) => typeof toolName === "string" && toolName.trim()
          )
        ),
      ]
    : [];
  return PAIRED_TOOL_DEPENDENCIES.reduce((tools, dependency) => {
    const withoutStandaloneChild = tools.filter(
      (toolName) => toolName !== dependency.childToolName
    );
    if (withoutStandaloneChild.includes(dependency.parentToolName)) {
      return [...withoutStandaloneChild, dependency.childToolName];
    }
    return withoutStandaloneChild;
  }, dedupedTools);
}

export const CREATE_PATH_MANUAL = "manual";
export const CREATE_PATH_AI = "ai";
export const CREATE_STAGE_IDLE = "idle";
export const CREATE_STAGE_CREATING_AGENT = "creating_agent";
export const CREATE_STAGE_STARTING_CHAT = "starting_chat";
export const AI_AGENT_DEFAULT_NAME = "New Agent";

const AI_AGENT_DEFAULT_DESCRIPTION =
  "Self-configuring AI agent created from natural-language requirements.";
const AI_AGENT_BOOTSTRAP_SYSTEM_PROMPT =
  "You are a self-configuring AI agent. Use your self-configuration tools to inspect and update your settings based on the user's requirements. Make focused configuration changes first, then continue helping the user.";
const INTERNAL_ONLY_TOOL_NAMES = new Set(["get_table"]);

// Minimal safety net for table-scope validation when the tool catalog
// fetch fails or is still loading. The catalog is the authoritative
// source, but these six tools MUST require configured tables.
export const TABLE_SCOPE_TOOL_NAMES = new Set([
  "add_column",
  "rename_column",
  "delete_column",
  "create_row",
  "update_row",
  "query_table",
]);

const KNOWLEDGE_INDEX_DEFAULT_STATUS = {
  state: "NO_KNOWLEDGE",
  updatedAt: null,
  lastIndexedAt: null,
  chunksIndexed: null,
  lastError: null,
  phase: null,
  progressPercent: null,
  sourcesTotal: null,
  sourcesProcessed: null,
  filesTotal: null,
  tablesTotal: null,
  hasManualContext: null,
  message: null,
};
const KNOWLEDGE_INDEX_STATE_COPY = {
  NO_KNOWLEDGE: "No knowledge configured yet.",
  INDEXING: "Knowledge index is running.",
  READY: "Knowledge index is ready.",
  FAILED: "Knowledge indexing failed.",
  STALE: "Knowledge changed and needs re-sync.",
};
const KNOWLEDGE_INDEX_STATE_LABELS = {
  NO_KNOWLEDGE: "No knowledge",
  INDEXING: "Indexing",
  READY: "Ready",
  FAILED: "Failed",
  STALE: "Stale",
};

function normalizeKnowledgeIndexStatus(status) {
  if (!status || typeof status !== "object") {
    return { ...KNOWLEDGE_INDEX_DEFAULT_STATUS };
  }
  const hasProgressValue =
    status.progressPercent !== null &&
    status.progressPercent !== undefined &&
    String(status.progressPercent).trim() !== "";
  const parsedProgress = hasProgressValue
    ? Number(status.progressPercent)
    : NaN;
  const normalizedProgress = Number.isFinite(parsedProgress)
    ? Math.max(0, Math.min(100, Math.round(parsedProgress)))
    : null;
  return {
    ...KNOWLEDGE_INDEX_DEFAULT_STATUS,
    ...status,
    state: String(
      status.state || KNOWLEDGE_INDEX_DEFAULT_STATUS.state
    ).toUpperCase(),
    progressPercent: normalizedProgress,
    phase: status.phase ? String(status.phase).toUpperCase() : null,
  };
}
const SESSION_TOOL_NAME = "get_session";
const SEND_SLACK_COMPONENT_TOOL_NAME = "send_slack_component";
const SEND_EMAIL_TOOL_NAME = "send_email";
const SLACK_WEBHOOK_URL_REGEX =
  /^https:\/\/hooks\.slack(?:-gov)?\.com\/services\/[A-Za-z0-9/_-]+$/;
const EXTERNAL_CHAT_ACCESS_MODE_DISABLED = "DISABLED";
const EXTERNAL_CHAT_ACCESS_MODE_PUBLIC = "PUBLIC";
const EXTERNAL_CHAT_ACCESS_MODE_EMAIL_MAGIC_LINK = "EMAIL_MAGIC_LINK";
const EXTERNAL_CHAT_ACCESS_MODE_AUTHENTICATED_USERS = "AUTHENTICATED_USERS";
const EXTERNAL_CHAT_ACCESS_MODE_AUTHENTICATED_SELECTED_USERS =
  "AUTHENTICATED_SELECTED_USERS";
const EXTERNAL_CHAT_ACCESS_MODES = new Set([
  EXTERNAL_CHAT_ACCESS_MODE_DISABLED,
  EXTERNAL_CHAT_ACCESS_MODE_PUBLIC,
  EXTERNAL_CHAT_ACCESS_MODE_EMAIL_MAGIC_LINK,
  EXTERNAL_CHAT_ACCESS_MODE_AUTHENTICATED_USERS,
  EXTERNAL_CHAT_ACCESS_MODE_AUTHENTICATED_SELECTED_USERS,
]);
const EXTERNAL_CHAT_DEFAULT_ACCESS_POLICY = {
  mode: EXTERNAL_CHAT_ACCESS_MODE_DISABLED,
  emailRestriction: "ANY",
  allowedEmails: [],
  allowedDomains: [],
  selectedUserIds: [],
};
const EXTERNAL_CHAT_MAX_SESSIONS_PER_IDENTITY_PRESETS = [
  1, 3, 5, 10, 25, 50, 100, 250, 500, 1000,
];
const EXTERNAL_CHAT_MAX_SESSIONS_TOTAL_PRESETS = [
  10, 25, 50, 100, 250, 500, 1000, 5000, 10000, 50000, 100000,
];
const EXTERNAL_CHAT_RATE_LIMIT_PERIOD_UNITS = [
  { value: "minutes", label: "minutes", seconds: 60 },
  { value: "hours", label: "hours", seconds: 3600 },
  { value: "days", label: "days", seconds: 86400 },
];
const EXTERNAL_CHAT_RATE_LIMIT_DEFAULT_PERIOD_UNIT = "hours";
const EXTERNAL_CHAT_RATE_LIMIT_DEFAULT_PERIOD_VALUE = "1";
const EXTERNAL_CHAT_RATE_LIMIT_DEFAULT_EXCEEDED_MESSAGE =
  "You've reached the message limit for now. Please try again later.";

const periodUnitToSeconds = (unit) =>
  EXTERNAL_CHAT_RATE_LIMIT_PERIOD_UNITS.find((u) => u.value === unit)
    ?.seconds || 60;

const splitPeriodSecondsIntoValueAndUnit = (totalSeconds) => {
  const seconds = Number(totalSeconds);
  if (!Number.isFinite(seconds) || seconds <= 0) {
    return {
      value: EXTERNAL_CHAT_RATE_LIMIT_DEFAULT_PERIOD_VALUE,
      unit: EXTERNAL_CHAT_RATE_LIMIT_DEFAULT_PERIOD_UNIT,
    };
  }
  for (const { value: unit, seconds: unitSeconds } of [
    ...EXTERNAL_CHAT_RATE_LIMIT_PERIOD_UNITS,
  ].reverse()) {
    if (seconds % unitSeconds === 0) {
      return { value: String(seconds / unitSeconds), unit };
    }
  }
  return {
    value: String(Math.max(1, Math.round(seconds / 60))),
    unit: "minutes",
  };
};
const EXTERNAL_CHAT_ACCESS_MODE_OPTIONS = [
  {
    value: EXTERNAL_CHAT_ACCESS_MODE_DISABLED,
    label: "Disabled (private only)",
    menuHint: "No public link access.",
    description: "Public chat link access is fully turned off.",
    detailLines: [
      "No end user can start or continue chat through a public link.",
      "Use this when the agent should stay private/internal only.",
    ],
  },
  {
    value: EXTERNAL_CHAT_ACCESS_MODE_PUBLIC,
    label: "Public (anyone with link)",
    menuHint: "No sign-in required.",
    description: "Anyone with the link can start a chat, subject to limits.",
    detailLines: [
      "No sign-in required.",
      "Identity is tracked as anonymous browser ID (with IP fallback).",
    ],
  },
  {
    value: EXTERNAL_CHAT_ACCESS_MODE_EMAIL_MAGIC_LINK,
    label: "Email verification (magic link)",
    menuHint: "User must verify email first.",
    description: "Users verify their email before they can start a chat.",
    detailLines: [
      "No account required.",
      "Each user must verify email by one-time magic link first.",
    ],
  },
  {
    value: EXTERNAL_CHAT_ACCESS_MODE_AUTHENTICATED_USERS,
    label: "Authenticated users",
    menuHint: "Any signed-in user can access.",
    description: "Only signed-in product users can access this link.",
    detailLines: [
      "Requires trusted signed-in identity.",
      "Any authenticated user can start a chat.",
    ],
  },
  {
    value: EXTERNAL_CHAT_ACCESS_MODE_AUTHENTICATED_SELECTED_USERS,
    label: "Authenticated selected users",
    menuHint: "Only selected signed-in users can access.",
    description: "Only specific signed-in users can access this link.",
    detailLines: [
      "Requires trusted signed-in identity.",
      "User must be in the selected users list.",
    ],
  },
];

const normalizeUniqueStringList = (values) =>
  Array.from(
    new Set(
      (Array.isArray(values) ? values : [])
        .map((value) => String(value || "").trim())
        .filter((value) => value.length > 0)
    )
  );

const normalizeExternalChatAccessMode = (value) => {
  const normalized = String(value || "")
    .trim()
    .toUpperCase();
  return EXTERNAL_CHAT_ACCESS_MODES.has(normalized)
    ? normalized
    : EXTERNAL_CHAT_ACCESS_MODE_DISABLED;
};

function buildExternalAccessPolicy(accessPolicy) {
  const source =
    accessPolicy && typeof accessPolicy === "object" ? accessPolicy : {};
  const mode = normalizeExternalChatAccessMode(source.mode);
  const emailRestrictionRaw = String(source.emailRestriction || "ANY")
    .trim()
    .toUpperCase();
  const emailRestriction =
    emailRestrictionRaw === "ALLOWLIST" || emailRestrictionRaw === "DOMAIN"
      ? emailRestrictionRaw
      : "ANY";
  const allowedEmails = parseAccessAllowlistEmails(source.allowedEmails || []);
  const allowedDomains = parseAccessAllowlistEmails(
    source.allowedDomains || []
  );
  const selectedUserIds = normalizeUniqueStringList(
    source.selectedUserIds || []
  );

  return {
    mode,
    emailRestriction:
      mode === EXTERNAL_CHAT_ACCESS_MODE_EMAIL_MAGIC_LINK
        ? emailRestriction
        : "ANY",
    allowedEmails:
      mode === EXTERNAL_CHAT_ACCESS_MODE_EMAIL_MAGIC_LINK &&
      emailRestriction === "ALLOWLIST"
        ? allowedEmails
        : [],
    allowedDomains:
      mode === EXTERNAL_CHAT_ACCESS_MODE_EMAIL_MAGIC_LINK &&
      emailRestriction === "DOMAIN"
        ? allowedDomains
        : [],
    selectedUserIds:
      mode === EXTERNAL_CHAT_ACCESS_MODE_AUTHENTICATED_SELECTED_USERS
        ? selectedUserIds
        : [],
  };
}

const parseSlackWebhookUrls = (value) =>
  normalizeUniqueStringList(String(value || "").split(/[\n,]/));

export const parseAccessAllowlistEmails = (value) =>
  Array.from(
    new Set(
      (Array.isArray(value) ? value : String(value || "").split(/[\n,]/))
        .flatMap((entry) => String(entry || "").split(/[\n,]/))
        .map((email) =>
          String(email || "")
            .trim()
            .toLowerCase()
        )
        .filter((email) => email.length > 0)
    )
  );

export const parseOptionalPositiveInt = (value) => {
  const normalized = String(value ?? "").trim();
  if (!normalized) return null;
  const parsed = Number(normalized);
  if (!Number.isFinite(parsed) || parsed <= 0) return null;
  return Math.floor(parsed);
};

const buildNumericLimitSelectOptions = (
  presetValues,
  currentValue,
  noLimitLabel = "No limit"
) => {
  const normalizedCurrent = String(currentValue ?? "").trim();
  const baseOptions = [
    { value: "", label: noLimitLabel },
    ...presetValues.map((value) => ({
      value: String(value),
      label: String(value),
    })),
  ];
  if (
    normalizedCurrent &&
    !baseOptions.some((option) => option.value === normalizedCurrent)
  ) {
    return [
      {
        value: normalizedCurrent,
        label: `Current custom (${normalizedCurrent})`,
      },
      ...baseOptions,
    ];
  }
  return baseOptions;
};

const getLimitOptionLabel = (options, value, fallback = "No limit") =>
  options.find((option) => option.value === String(value ?? ""))?.label ||
  fallback;

const normalizeConfiguredTables = (values) => {
  const deduped = new Map();
  (Array.isArray(values) ? values : []).forEach((value) => {
    const tableId = String(
      value?.tableId || value?.databaseId || value?.id || ""
    ).trim();
    if (!tableId || deduped.has(tableId)) return;
    const tableName = String(
      value?.tableName || value?.name || value?.databaseName || tableId
    ).trim();
    deduped.set(tableId, {
      tableId,
      tableName: tableName || tableId,
    });
  });
  return Array.from(deduped.values());
};

const normalizeConfiguredAgents = (values) => {
  const deduped = new Map();
  (Array.isArray(values) ? values : []).forEach((value) => {
    const selectedAgentId = String(value?.agentId || value?.id || "").trim();
    if (!selectedAgentId || deduped.has(selectedAgentId)) return;
    deduped.set(selectedAgentId, {
      agentId: selectedAgentId,
      name: String(value?.name || selectedAgentId).trim() || selectedAgentId,
      description: String(value?.description || "").trim(),
    });
  });
  return Array.from(deduped.values());
};

const normalizeExamplePrompts = (values) =>
  (Array.isArray(values) ? values : [])
    .map((value) => ({
      title: String(value?.title || "")
        .trim()
        .slice(0, 80),
      prompt: String(value?.prompt || "")
        .trim()
        .slice(0, 500),
    }))
    .filter((value) => value.prompt.length > 0)
    .slice(0, 6);

const normalizeKnowledgeTables = (values) => {
  const deduped = new Map();
  (Array.isArray(values) ? values : []).forEach((value) => {
    const databaseId = String(
      value?.databaseId || value?.tableId || value?.id || ""
    ).trim();
    if (!databaseId || deduped.has(databaseId)) return;
    const tableName = String(
      value?.tableName || value?.name || value?.databaseName || databaseId
    ).trim();
    deduped.set(databaseId, {
      databaseId,
      tableName: tableName || databaseId,
    });
  });
  return Array.from(deduped.values());
};

const normalizeReviewTaskUsers = (users) => {
  if (!Array.isArray(users)) return [];
  const seen = new Set();
  return users
    .filter((user) => user && user.roleDisabled !== true)
    .filter((user) => {
      const userName = String(user.userName || "").trim();
      if (!userName || seen.has(userName)) return false;
      seen.add(userName);
      return true;
    })
    .map((user) => ({
      ...user,
      userName: String(user.userName || "").trim(),
      displayName: String(
        user.displayName || user.name || user.fullName || user.userName || ""
      ).trim(),
      email: String(user.email || "").trim(),
    }));
};

const buildSlackWebhookValidationError = (urls) => {
  if (!Array.isArray(urls) || urls.length === 0) {
    return "Add at least one Slack webhook URL.";
  }
  const invalidUrl = urls.find((url) => !SLACK_WEBHOOK_URL_REGEX.test(url));
  if (invalidUrl) {
    return `Invalid Slack webhook URL format: ${invalidUrl}`;
  }
  return "";
};

const extractSaveErrorMessage = (error, fallback) => {
  const responseData = error?.response?.data;
  if (typeof responseData === "string" && responseData.trim()) {
    return responseData.trim();
  }
  if (responseData && typeof responseData.message === "string") {
    const message = responseData.message.trim();
    if (message) return message;
  }
  if (typeof error?.message === "string" && error.message.trim()) {
    return error.message.trim();
  }
  if (typeof fallback === "string" && fallback.trim()) {
    return fallback.trim();
  }
  return "We couldn't save this agent configuration. Check required tool settings and try again.";
};

export function normalizeAiRequirementsInput(value) {
  return String(value || "").trim();
}

export function deriveAiAgentName(requirements) {
  const normalized = normalizeAiRequirementsInput(requirements);
  if (!normalized) return AI_AGENT_DEFAULT_NAME;

  const firstNonEmptyLine =
    normalized.split("\n").find((line) => line.trim().length > 0) || "";
  const sanitized = firstNonEmptyLine
    .replace(/^[-*0-9.)\s]+/, "")
    .replace(/[.,:;!?()[\]{}"'`]/g, "")
    .trim();
  if (!sanitized) return AI_AGENT_DEFAULT_NAME;

  const candidate = sanitized.split(/\s+/).slice(0, 6).join(" ");
  if (!candidate) return AI_AGENT_DEFAULT_NAME;
  return candidate.slice(0, 56).trim();
}

export function buildSelfConfigurationKickoffMessage(requirements) {
  const normalized = normalizeAiRequirementsInput(requirements);
  if (!normalized) return "";

  return [
    "You are configuring your own agent settings based on the user's requirements.",
    "Follow this sequence to ensure an accurate setup:",
    "1) Call get_self_config first to inspect current settings.",
    "2) Review availableToolCatalog from get_self_config and only use those toolName values when setting enabledTools.",
    "3) Determine the smallest set of configuration changes needed.",
    "4) Apply updates with update_self_config only after inspection.",
    "5) Explain exactly what you changed and how it satisfies requirements.",
    "6) If any requirement is ambiguous, ask concise clarifying questions before risky edits.",
    "",
    "User requirements:",
    normalized,
    "",
    "Start by reading your current configuration now.",
  ].join("\n");
}

function applyPairedToolHierarchy(tools) {
  if (!Array.isArray(tools) || tools.length === 0) {
    return [];
  }
  let normalizedTools = tools.map((tool) => ({ ...tool }));

  PAIRED_TOOL_DEPENDENCIES.forEach((dependency) => {
    const parentIndex = normalizedTools.findIndex(
      (tool) => tool.name === dependency.parentToolName
    );
    const childIndex = normalizedTools.findIndex(
      (tool) => tool.name === dependency.childToolName
    );
    if (childIndex < 0) {
      return;
    }

    const childTool = {
      ...normalizedTools[childIndex],
      parentToolName: dependency.parentToolName,
      disableStandaloneToggle: true,
    };
    normalizedTools.splice(childIndex, 1);

    if (parentIndex < 0) {
      normalizedTools.push(childTool);
      return;
    }

    const nextParentIndex = normalizedTools.findIndex(
      (tool) => tool.name === dependency.parentToolName
    );
    normalizedTools.splice(nextParentIndex + 1, 0, childTool);
  });

  return normalizedTools;
}

const HIDDEN_TOOL_CATEGORIES = new Set([
  "Workers",
  "Workflow Builder",
  "Components",
]);

const CATEGORY_RENAMES = {
  Actions: "General",
};

export const mapToolCatalogToCategories = (catalog) => {
  if (!Array.isArray(catalog) || catalog.length === 0) return [];

  const grouped = new Map();
  catalog.forEach((entry) => {
    const toolName = entry?.toolName || entry?.function?.name || entry?.name;
    if (!toolName) return;
    if (INTERNAL_ONLY_TOOL_NAMES.has(toolName)) return;
    const fallbackLabel =
      TOOL_LABEL_OVERRIDES[toolName] || formatToolNameAsLabel(toolName);
    const normalizedCategory =
      toolName === SESSION_TOOL_NAME
        ? "Workflows"
        : entry?.category || (entry?.toolName ? "General" : "Available Tools");
    const normalizedLabel =
      toolName === SESSION_TOOL_NAME
        ? "Get Workflow Sessions"
        : TOOL_LABEL_OVERRIDES[toolName] ||
          entry?.displayName ||
          entry?.label ||
          fallbackLabel;
    const rawCategory =
      normalizedCategory || (entry?.toolName ? "General" : "Available Tools");
    if (HIDDEN_TOOL_CATEGORIES.has(rawCategory)) return;
    const category = CATEGORY_RENAMES[rawCategory] || rawCategory;
    if (!grouped.has(category)) grouped.set(category, []);
    grouped.get(category).push({
      name: toolName,
      label: normalizedLabel,
      desc:
        entry?.description || entry?.desc || entry?.function?.description || "",
      iconName: entry?.iconName || null,
      parentToolName: entry?.parentToolName || null,
      requiresTableScope: Boolean(entry?.requiresTableScope),
      disableStandaloneToggle: Boolean(entry?.disableStandaloneToggle),
    });
  });

  return Array.from(grouped.entries())
    .map(([title, tools]) => ({
      title,
      tools: applyPairedToolHierarchy(
        tools.sort((a, b) => a.label.localeCompare(b.label))
      ),
    }))
    .filter(
      (category) => Array.isArray(category.tools) && category.tools.length
    )
    .sort((a, b) => a.title.localeCompare(b.title));
};

export const filterToolCategories = (categories, rawQuery) => {
  if (!Array.isArray(categories) || categories.length === 0) return [];
  const query = String(rawQuery || "")
    .trim()
    .toLowerCase();
  if (!query) return categories;

  return categories
    .map((category) => {
      const tools = Array.isArray(category?.tools) ? category.tools : [];
      const filteredTools = tools.filter((tool) =>
        [tool?.label, tool?.name, tool?.desc]
          .map((value) => String(value || "").toLowerCase())
          .join(" ")
          .includes(query)
      );
      return {
        ...category,
        tools: filteredTools,
      };
    })
    .filter((category) => category.tools.length > 0);
};

export const COMPLETION_NOTIFICATION_TYPES = [
  { value: "NONE", label: "None" },
  { value: "EMAIL", label: "Email" },
  { value: "SLACK", label: "Slack" },
  { value: "WEBHOOK", label: "Custom POST" },
];

const EMAIL_RESPONSE_POLICY_PRESET_ALWAYS_REPLY_EMAIL = "ALWAYS_REPLY_EMAIL";
const DEFAULT_EMAIL_RESPONSE_POLICY_PRESET =
  EMAIL_RESPONSE_POLICY_PRESET_ALWAYS_REPLY_EMAIL;
export const ensureSendEmailEnabledForPolicy = (enabledTools, policyPreset) => {
  const normalizedTools = Array.isArray(enabledTools)
    ? enabledTools.filter(
        (toolName) => typeof toolName === "string" && toolName
      )
    : [];
  const dedupedTools = [...new Set(normalizedTools)];
  if (
    policyPreset === EMAIL_RESPONSE_POLICY_PRESET_ALWAYS_REPLY_EMAIL &&
    !dedupedTools.includes(SEND_EMAIL_TOOL_NAME)
  ) {
    dedupedTools.push(SEND_EMAIL_TOOL_NAME);
  }
  return dedupedTools;
};
const EMAIL_RESPONSE_POLICY_PRESETS = [
  {
    value: EMAIL_RESPONSE_POLICY_PRESET_ALWAYS_REPLY_EMAIL,
    label: "Always reply back via email",
    description:
      "Use email as the default response channel for inbound email messages.",
  },
  {
    value: "ONLY_REPLY_CHAT",
    label: "Only reply in chat",
    description:
      "Never send outbound email unless a manager explicitly asks for it.",
  },
  {
    value: "AWAIT_INSTRUCTIONS_BEFORE_EMAIL",
    label: "Await instructions before emailing",
    description:
      "Draft in chat first, then wait for explicit instruction before sending email.",
  },
];

export const buildWebhookPayloadPreview = (agentName) => ({
  event: "agent_response_completed",
  agent: {
    id: "<agent_id>",
    name: agentName || "<agent_name>",
  },
  session: {
    id: "<chat_session_id>",
    status: "COMPLETED",
    iterationsUsed: 3,
    source: "CHAT",
    workflowSessionId: "<workflow_session_id_or_null>",
    completedAt: "2026-02-19T20:11:27Z",
    startedAt: "2026-02-19T20:10:52Z",
  },
  response: {
    preview: "<assistant_response_preview>",
    length: 138,
  },
  links: {
    agentSession:
      "https://app.workflow86.com/agents/<agent_id>?sessionId=<chat_session_id>",
    workflowSession:
      "https://app.workflow86.com/project/logs/progress_view/<project_id>/<workflow_session_id>?test=false",
  },
});

export default function AgentConfigDrawer({
  open,
  onClose,
  agentId,
  onSaved,
  onCreated,
}) {
  const navigate = useNavigate();
  const isCreateMode = !agentId;
  const [isSystemAgent, setIsSystemAgent] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [systemPrompt, setSystemPrompt] = useState("");
  const [model, setModel] = useState(DEFAULT_MODEL);
  const [maxIterations, setMaxIterations] = useState(50);
  const [selfConfigurationMode, setSelfConfigurationMode] = useState(
    SELF_CONFIGURATION_MODE_AWARENESS_AND_EDIT
  );
  const [enabledTools, setEnabledTools] = useState([...DEFAULT_ENABLED_TOOLS]);
  const [toolInstructions, setToolInstructions] = useState({});
  const [sendSlackWebhookUrlsInput, setSendSlackWebhookUrlsInput] =
    useState("");
  const [sendSlackWebhookValidationError, setSendSlackWebhookValidationError] =
    useState("");
  const [toolCategories, setToolCategories] = useState([]);

  const tableScopedToolNames = useMemo(() => {
    const set = new Set(TABLE_SCOPE_TOOL_NAMES);
    toolCategories.forEach((cat) =>
      (cat.tools || []).forEach((t) => {
        if (t.requiresTableScope) set.add(t.name);
      })
    );
    return set;
  }, [toolCategories]);
  const [toolSearchQuery, setToolSearchQuery] = useState("");
  const [loadingToolCatalog, setLoadingToolCatalog] = useState(false);
  const [runWorkflowConfigs, setRunWorkflowConfigs] = useState([]);
  const [workflowScopeMode, setWorkflowScopeMode] = useState("SELECTED");
  const [runWorkflowValidationError, setRunWorkflowValidationError] =
    useState("");
  const [configuredTables, setConfiguredTables] = useState([]);
  const [configuredAgents, setConfiguredAgents] = useState([]);
  const [tableScopeMode, setTableScopeMode] = useState("SELECTED");
  const [agentScopeMode, setAgentScopeMode] = useState("SELECTED");
  const [fileScopeMode, setFileScopeMode] = useState("SELECTED");
  const [artifactScopeMode, setArtifactScopeMode] = useState("SELECTED");
  const [configuredTablesValidationError, setConfiguredTablesValidationError] =
    useState("");
  const [workflowOptions, setWorkflowOptions] = useState([]);
  const [loadingWorkflowOptions, setLoadingWorkflowOptions] = useState(false);
  const [agentOptions, setAgentOptions] = useState([]);
  const [loadingAgentOptions, setLoadingAgentOptions] = useState(false);
  const [completionNotificationType, setCompletionNotificationType] =
    useState("NONE");
  const [completionNotifyEmails, setCompletionNotifyEmails] = useState("");
  const [completionSlackWebhookUrl, setCompletionSlackWebhookUrl] =
    useState("");
  const [completionWebhookUrl, setCompletionWebhookUrl] = useState("");
  const knowledgeRetrievalMode = "RAG";
  const [knowledgeFiles, setKnowledgeFiles] = useState([]);
  const [knowledgeTables, setKnowledgeTables] = useState([]);
  const [knowledgeManualContext, setKnowledgeManualContext] = useState("");
  const [knowledgeIndexStatus, setKnowledgeIndexStatus] = useState(
    KNOWLEDGE_INDEX_DEFAULT_STATUS
  );
  const [inboxAddress, setInboxAddress] = useState("");
  const [allowAllSenders, setAllowAllSenders] = useState(false);
  const [acceptEmailAttachments, setAcceptEmailAttachments] = useState(false);
  const [senderWhitelist, setSenderWhitelist] = useState("");
  const [emailResponsePolicyPreset, setEmailResponsePolicyPreset] = useState(
    DEFAULT_EMAIL_RESPONSE_POLICY_PRESET
  );
  const [emailResponsePolicyCustomPrompt, setEmailResponsePolicyCustomPrompt] =
    useState("");
  const [externalAccessPolicy, setExternalAccessPolicy] = useState(
    EXTERNAL_CHAT_DEFAULT_ACCESS_POLICY
  );
  const [
    externalChatMaxSessionsPerIdentity,
    setExternalChatMaxSessionsPerIdentity,
  ] = useState("");
  const [externalChatMaxSessionsTotal, setExternalChatMaxSessionsTotal] =
    useState("");
  const [externalChatRateLimitMessages, setExternalChatRateLimitMessages] =
    useState("");
  const [
    externalChatRateLimitPeriodValue,
    setExternalChatRateLimitPeriodValue,
  ] = useState(EXTERNAL_CHAT_RATE_LIMIT_DEFAULT_PERIOD_VALUE);
  const [externalChatRateLimitPeriodUnit, setExternalChatRateLimitPeriodUnit] =
    useState(EXTERNAL_CHAT_RATE_LIMIT_DEFAULT_PERIOD_UNIT);
  const [
    externalChatRateLimitExceededMessage,
    setExternalChatRateLimitExceededMessage,
  ] = useState("");
  const [reviewEscalationPolicy, setReviewEscalationPolicy] = useState("");
  const [blockedWhileReviewMessage, setBlockedWhileReviewMessage] =
    useState("");
  const [
    customBlockedWhileReviewMessageEnabled,
    setCustomBlockedWhileReviewMessageEnabled,
  ] = useState(false);
  const [autoReplyOnBlockedIngress, setAutoReplyOnBlockedIngress] =
    useState(true);
  const [reviewTaskAssigneeUserId, setReviewTaskAssigneeUserId] = useState("");
  const [
    reviewTaskEmailNotificationEnabled,
    setReviewTaskEmailNotificationEnabled,
  ] = useState(false);
  const [reviewTaskEligibleUsers, setReviewTaskEligibleUsers] = useState([]);
  const [loadingReviewTaskEligibleUsers, setLoadingReviewTaskEligibleUsers] =
    useState(false);
  const [publicShareLink, setPublicShareLink] = useState(null);
  const [publicShareLinkError, setPublicShareLinkError] = useState("");
  const [creatingPublicShareLink, setCreatingPublicShareLink] = useState(false);
  const [revokingPublicShareLink, setRevokingPublicShareLink] = useState(false);
  const [publicShareLinkExpiresAt, setPublicShareLinkExpiresAt] = useState("");
  const [enablePublicShareLinkExpiry, setEnablePublicShareLinkExpiry] =
    useState(false);
  const [showSystemPromptInChat, setShowSystemPromptInChat] = useState(true);
  const [showToolCallsAndOutputsInChat, setShowToolCallsAndOutputsInChat] =
    useState(true);
  const [showInterAgentMessagesInChat, setShowInterAgentMessagesInChat] =
    useState(true);
  const [showWorkflowProvenanceInChat, setShowWorkflowProvenanceInChat] =
    useState(true);
  const [showSchedulingActivityInChat, setShowSchedulingActivityInChat] =
    useState(true);
  const [
    showSessionOperationalMetaInChat,
    setShowSessionOperationalMetaInChat,
  ] = useState(true);
  const [
    showSupervisorControlMessagesInChat,
    setShowSupervisorControlMessagesInChat,
  ] = useState(true);
  const [enableHumanTakeoverMode, setEnableHumanTakeoverMode] = useState(false);
  const [showSharedSessionsSidebar, setShowSharedSessionsSidebar] =
    useState(true);
  const [examplePrompts, setExamplePrompts] = useState([]);
  const [newWhitelistEntry, setNewWhitelistEntry] = useState("");
  const [uploadingFile, setUploadingFile] = useState(false);
  const [indexingKnowledge, setIndexingKnowledge] = useState(false);
  const [deletingKnowledgeIndex, setDeletingKnowledgeIndex] = useState(false);
  const [databaseList, setDatabaseList] = useState([]);
  const [loadingDatabases, setLoadingDatabases] = useState(false);
  const [activeTab, setActiveTab] = useState("general");
  const [saving, setSaving] = useState(false);
  const [saveResult, setSaveResult] = useState(null); // 'success' | 'error' | null
  const [saveErrorMessage, setSaveErrorMessage] = useState("");
  const [creationPath, setCreationPath] = useState(null);
  const [aiRequirements, setAiRequirements] = useState("");
  const [creationStage, setCreationStage] = useState(CREATE_STAGE_IDLE);
  const [creationError, setCreationError] = useState("");
  const [loaded, setLoaded] = useState(false);
  const [globalVarPlaceholders, setGlobalVarPlaceholders] = useState([]);
  const [allAgents, setAllAgents] = useState([]);
  const webhookPayloadPreview = buildWebhookPayloadPreview(name);
  const isAiCreatePath = isCreateMode && creationPath === CREATE_PATH_AI;
  const isManualCreatePath =
    isCreateMode && creationPath === CREATE_PATH_MANUAL;
  const showCreationPathChooser = isCreateMode && !creationPath;
  const showManualConfigurationLayout = !isCreateMode || isManualCreatePath;
  const normalizedAiRequirements = normalizeAiRequirementsInput(aiRequirements);

  useEffect(() => {
    if (open && !loaded) {
      if (agentId) {
        useAgentStore
          .getState()
          .fetchAgent(agentId)
          .then((data) => {
            if (data) {
              const loadedEnabledTools = (data.enabledTools || []).filter(
                (toolName) => !INTERNAL_ONLY_TOOL_NAMES.has(toolName)
              );
              const loadedMode = normalizeSelfConfigurationMode(
                data.selfConfigurationMode
              );
              const derivedModeFromTools =
                deriveSelfConfigurationModeFromEnabledTools(loadedEnabledTools);
              const resolvedSelfConfigurationMode =
                isSelfConfigurationEditEnabled(loadedMode) ||
                isSelfConfigurationEditEnabled(derivedModeFromTools)
                  ? SELF_CONFIGURATION_MODE_AWARENESS_AND_EDIT
                  : isSelfConfigurationAwarenessEnabled(loadedMode) ||
                    isSelfConfigurationAwarenessEnabled(derivedModeFromTools)
                  ? SELF_CONFIGURATION_MODE_AWARENESS_ONLY
                  : SELF_CONFIGURATION_MODE_OFF;
              setIsSystemAgent(data.systemAgent === true);
              setName(data.name || "");
              setDescription(data.description || "");
              setSystemPrompt(data.systemPrompt || "");
              setModel(data.model || DEFAULT_MODEL);
              setMaxIterations(data.maxIterations || 50);
              setSelfConfigurationMode(resolvedSelfConfigurationMode);
              setEnabledTools(
                ensureSelfConfigurationToolsForMode(
                  ensureTaskQueuePlannerToolsPaired(loadedEnabledTools),
                  resolvedSelfConfigurationMode
                )
              );
              setToolInstructions(data.toolInstructions || {});
              setSendSlackWebhookUrlsInput(
                normalizeUniqueStringList(data.sendSlackWebhookUrls).join("\n")
              );
              setSendSlackWebhookValidationError("");
              setRunWorkflowConfigs(data.runWorkflowConfigs || []);
              setWorkflowScopeMode(data.workflowScopeMode || "SELECTED");
              setRunWorkflowValidationError("");
              setConfiguredTables(
                normalizeConfiguredTables(data.configuredTables || [])
              );
              setConfiguredAgents(
                normalizeConfiguredAgents(data.configuredAgents || [])
              );
              setExamplePrompts(normalizeExamplePrompts(data.examplePrompts));
              setTableScopeMode(data.tableScopeMode || "SELECTED");
              setAgentScopeMode(data.agentScopeMode || "SELECTED");
              setFileScopeMode(data.fileScopeMode || "SELECTED");
              setArtifactScopeMode(data.artifactScopeMode || "SELECTED");
              setConfiguredTablesValidationError("");
              setSaveErrorMessage("");
              setCompletionNotificationType(
                data.completionNotificationType || "NONE"
              );
              setCompletionNotifyEmails(
                (data.completionNotifyEmails || []).join(", ")
              );
              setCompletionSlackWebhookUrl(
                data.completionSlackWebhookUrl || ""
              );
              setCompletionWebhookUrl(data.completionWebhookUrl || "");
              const k = data.knowledge || {};
              setKnowledgeFiles(k.files || []);
              setKnowledgeTables(normalizeKnowledgeTables(k.tables || []));
              setKnowledgeManualContext(k.manualContext || "");
              setKnowledgeIndexStatus(
                normalizeKnowledgeIndexStatus(data.knowledgeIndexStatus)
              );
              setInboxAddress(data.inboxAddress || "");
              setAllowAllSenders(data.allowAllSenders || false);
              setAcceptEmailAttachments(data.acceptEmailAttachments || false);
              setSenderWhitelist((data.senderWhitelist || []).join("\n"));
              setEmailResponsePolicyPreset(
                data.emailResponsePolicyPreset ||
                  DEFAULT_EMAIL_RESPONSE_POLICY_PRESET
              );
              setEmailResponsePolicyCustomPrompt(
                data.emailResponsePolicyCustomPrompt || ""
              );
              setExternalAccessPolicy(
                buildExternalAccessPolicy(data.externalAccessPolicy)
              );
              setExternalChatMaxSessionsPerIdentity(
                data.externalChatMaxSessionsPerIdentity
                  ? String(data.externalChatMaxSessionsPerIdentity)
                  : ""
              );
              setExternalChatMaxSessionsTotal(
                data.externalChatMaxSessionsTotal
                  ? String(data.externalChatMaxSessionsTotal)
                  : ""
              );
              setExternalChatRateLimitMessages(
                data.externalChatRateLimitMessages
                  ? String(data.externalChatRateLimitMessages)
                  : ""
              );
              const loadedPeriod = splitPeriodSecondsIntoValueAndUnit(
                data.externalChatRateLimitPeriodSeconds
              );
              setExternalChatRateLimitPeriodValue(loadedPeriod.value);
              setExternalChatRateLimitPeriodUnit(loadedPeriod.unit);
              setExternalChatRateLimitExceededMessage(
                data.externalChatRateLimitExceededMessage || ""
              );
              setReviewEscalationPolicy(data.reviewEscalationPolicy || "");
              const loadedBlockedWhileReviewMessage =
                data.blockedWhileReviewMessage || "";
              setBlockedWhileReviewMessage(loadedBlockedWhileReviewMessage);
              setCustomBlockedWhileReviewMessageEnabled(
                loadedBlockedWhileReviewMessage.trim().length > 0
              );
              setAutoReplyOnBlockedIngress(
                data.autoReplyOnBlockedIngress !== false
              );
              setReviewTaskAssigneeUserId(data.reviewTaskAssigneeUserId || "");
              setReviewTaskEmailNotificationEnabled(
                data.reviewTaskEmailNotificationEnabled === true
              );
              setPublicShareLinkError("");
              usePublicChatStore
                .getState()
                .getLatestPublicChatShareLink(agentId)
                .then((latestShareLink) => {
                  if (latestShareLink?.shareId) {
                    setPublicShareLink(latestShareLink);
                  } else {
                    setPublicShareLink(null);
                  }
                });
              setPublicShareLinkExpiresAt("");
              setEnablePublicShareLinkExpiry(false);
              const chatUiPolicy = data.chatUiPolicy || {};
              setShowSystemPromptInChat(
                chatUiPolicy.showSystemPromptInChat !== false
              );
              setShowToolCallsAndOutputsInChat(
                chatUiPolicy.showToolCallsAndOutputsInChat !== false
              );
              setShowInterAgentMessagesInChat(
                chatUiPolicy.showInterAgentMessagesInChat !== false
              );
              setShowWorkflowProvenanceInChat(
                chatUiPolicy.showWorkflowProvenanceInChat !== false
              );
              setShowSchedulingActivityInChat(
                chatUiPolicy.showSchedulingActivityInChat !== false
              );
              setShowSessionOperationalMetaInChat(
                chatUiPolicy.showSessionOperationalMetaInChat !== false
              );
              setShowSupervisorControlMessagesInChat(
                chatUiPolicy.showSupervisorControlMessagesInChat !== false
              );
              setEnableHumanTakeoverMode(
                chatUiPolicy.enableHumanTakeoverMode === true
              );
              setShowSharedSessionsSidebar(
                chatUiPolicy.showSharedSessionsSidebar !== false
              );
            } else {
              setPublicShareLink(null);
              setPublicShareLinkError("");
              setKnowledgeIndexStatus({ ...KNOWLEDGE_INDEX_DEFAULT_STATUS });
            }
            setLoaded(true);
          });
      } else {
        setLoaded(true);
      }
    }
  }, [open, agentId, loaded]);

  useEffect(() => {
    if (open) {
      setLoadingToolCatalog(true);
      useAgentStore
        .getState()
        .fetchToolCatalog()
        .then((catalog) => {
          setToolCategories(mapToolCatalogToCategories(catalog));
        })
        .catch(() => {
          setToolCategories([]);
        })
        .finally(() => setLoadingToolCatalog(false));
      setLoadingWorkflowOptions(true);
      useAgentStore
        .getState()
        .fetchWorkflowOptions()
        .then((options) =>
          setWorkflowOptions(Array.isArray(options) ? options : [])
        )
        .catch(() => setWorkflowOptions([]))
        .finally(() => setLoadingWorkflowOptions(false));
      setLoadingAgentOptions(true);
      send_request("project-service/agent/list", "", {}, "GET")
        .then((res) => {
          const list = Array.isArray(res?.data) ? res.data : [];
          setAgentOptions(list.filter((agent) => agent?.systemAgent !== true));
        })
        .catch(() => setAgentOptions([]))
        .finally(() => setLoadingAgentOptions(false));

      send_request("component-service/global-variables", "", {}, "get")
        .then((res) => {
          const vars = res?.data || [];
          setGlobalVarPlaceholders(
            vars.map((v) => ({ key: `${v.name}.GLOBAL`, type: "text" }))
          );
        })
        .catch(() => {});
      setLoadingDatabases(true);
      send_request("data-service/v2/table", "", {}, "GET")
        .then((res) => {
          const v2 = Array.isArray(res?.data)
            ? res.data
            : Array.isArray(res)
            ? res
            : [];
          setDatabaseList(
            v2.map((t) => ({
              databaseId: t.tableId,
              tableId: t.tableId,
              name: t.tableName,
              tableName: t.tableName,
            }))
          );
        })
        .catch(() => setDatabaseList([]))
        .finally(() => setLoadingDatabases(false));
      setLoadingReviewTaskEligibleUsers(true);
      send_request("authz-service/get-active-users", "", "", "get")
        .then((res) =>
          setReviewTaskEligibleUsers(normalizeReviewTaskUsers(res?.data))
        )
        .catch(() => setReviewTaskEligibleUsers([]))
        .finally(() => setLoadingReviewTaskEligibleUsers(false));
      useAgentStore
        .getState()
        .refreshAgents()
        .then(() => {
          setAllAgents(
            useAgentStore.getState().agents.filter((a) => a.agentId !== agentId)
          );
        });
    }
  }, [open, agentId]);

  useEffect(() => {
    if (!open) {
      setLoaded(false);
      setActiveTab("general");
      setName("");
      setDescription("");
      setSystemPrompt("");
      setModel(DEFAULT_MODEL);
      setMaxIterations(10);
      setSelfConfigurationMode(SELF_CONFIGURATION_MODE_AWARENESS_AND_EDIT);
      setEnabledTools([...DEFAULT_ENABLED_TOOLS]);
      setToolInstructions({});
      setSendSlackWebhookUrlsInput("");
      setSendSlackWebhookValidationError("");
      setRunWorkflowConfigs([]);
      setWorkflowScopeMode("SELECTED");
      setRunWorkflowValidationError("");
      setConfiguredTables([]);
      setConfiguredAgents([]);
      setExamplePrompts([]);
      setTableScopeMode("SELECTED");
      setAgentScopeMode("SELECTED");
      setFileScopeMode("SELECTED");
      setArtifactScopeMode("SELECTED");
      setConfiguredTablesValidationError("");
      setCompletionNotificationType("NONE");
      setCompletionNotifyEmails("");
      setCompletionSlackWebhookUrl("");
      setCompletionWebhookUrl("");
      setKnowledgeFiles([]);
      setKnowledgeTables([]);
      setKnowledgeManualContext("");
      setKnowledgeIndexStatus({ ...KNOWLEDGE_INDEX_DEFAULT_STATUS });
      setInboxAddress("");
      setAllowAllSenders(false);
      setAcceptEmailAttachments(false);
      setSenderWhitelist("");
      setEmailResponsePolicyPreset(DEFAULT_EMAIL_RESPONSE_POLICY_PRESET);
      setEmailResponsePolicyCustomPrompt("");
      setExternalAccessPolicy(EXTERNAL_CHAT_DEFAULT_ACCESS_POLICY);
      setExternalChatMaxSessionsPerIdentity("");
      setExternalChatMaxSessionsTotal("");
      setExternalChatRateLimitMessages("");
      setExternalChatRateLimitPeriodValue(
        EXTERNAL_CHAT_RATE_LIMIT_DEFAULT_PERIOD_VALUE
      );
      setExternalChatRateLimitPeriodUnit(
        EXTERNAL_CHAT_RATE_LIMIT_DEFAULT_PERIOD_UNIT
      );
      setExternalChatRateLimitExceededMessage("");
      setReviewEscalationPolicy("");
      setBlockedWhileReviewMessage("");
      setCustomBlockedWhileReviewMessageEnabled(false);
      setAutoReplyOnBlockedIngress(true);
      setReviewTaskAssigneeUserId("");
      setReviewTaskEmailNotificationEnabled(false);
      setReviewTaskEligibleUsers([]);
      setLoadingReviewTaskEligibleUsers(false);
      setPublicShareLink(null);
      setPublicShareLinkError("");
      setCreatingPublicShareLink(false);
      setRevokingPublicShareLink(false);
      setPublicShareLinkExpiresAt("");
      setEnablePublicShareLinkExpiry(false);
      setShowSystemPromptInChat(true);
      setShowToolCallsAndOutputsInChat(true);
      setShowInterAgentMessagesInChat(true);
      setShowWorkflowProvenanceInChat(true);
      setShowSchedulingActivityInChat(true);
      setShowSessionOperationalMetaInChat(true);
      setShowSupervisorControlMessagesInChat(true);
      setEnableHumanTakeoverMode(false);
      setShowSharedSessionsSidebar(true);
      setSaveResult(null);
      setSaveErrorMessage("");
      setCreationPath(null);
      setAiRequirements("");
      setCreationStage(CREATE_STAGE_IDLE);
      setCreationError("");
      setDeleteConfirmText("");
      setToolCategories([]);
      setToolSearchQuery("");
      setLoadingToolCatalog(false);
      setWorkflowOptions([]);
      setLoadingWorkflowOptions(false);
      setAgentOptions([]);
      setLoadingAgentOptions(false);
      setUploadingFile(false);
      setIndexingKnowledge(false);
      setDeletingKnowledgeIndex(false);
    }
  }, [open]);

  useEffect(() => {
    const requiresConfiguredTables = enabledTools.some((toolName) =>
      tableScopedToolNames.has(toolName)
    );
    if (
      tableScopeMode === "ALL" ||
      !requiresConfiguredTables ||
      configuredTables.length > 0
    ) {
      setConfiguredTablesValidationError("");
    }
  }, [enabledTools, configuredTables, tableScopeMode]);

  useEffect(() => {
    const requiresRunWorkflow = enabledTools.includes("run_workflow");
    if (
      workflowScopeMode === "ALL" ||
      !requiresRunWorkflow ||
      runWorkflowConfigs.length > 0
    ) {
      setRunWorkflowValidationError("");
    }
  }, [enabledTools, runWorkflowConfigs, workflowScopeMode]);

  const toggleTool = (toolName) => {
    if (toolName === TASK_QUEUE_UPDATE_TOOL_NAME) {
      return;
    }
    if (
      toolName === SEND_SLACK_COMPONENT_TOOL_NAME &&
      enabledTools.includes(toolName)
    ) {
      setSendSlackWebhookValidationError("");
    }
    setEnabledTools((prev) => {
      const toggledTools = prev.includes(toolName)
        ? prev.filter((t) => t !== toolName)
        : [...prev, toolName];
      const pairedTools = ensureTaskQueuePlannerToolsPaired(toggledTools);
      const nextMode = deriveSelfConfigurationModeFromEnabledTools(pairedTools);
      const syncedTools = ensureSelfConfigurationToolsForMode(
        pairedTools,
        nextMode
      );
      setSelfConfigurationMode(nextMode);
      return syncedTools;
    });
  };

  const handleFileDrop = useCallback(
    async (acceptedFiles) => {
      if (!agentId || uploadingFile) return;
      const remaining = MAX_KNOWLEDGE_FILES - knowledgeFiles.length;
      const filesToUpload = acceptedFiles.slice(0, remaining);
      for (const file of filesToUpload) {
        setUploadingFile(true);
        try {
          const result = await useAgentKnowledgeStore
            .getState()
            .uploadKnowledgeFile(agentId, file);
          if (result?.knowledge?.files) {
            setKnowledgeFiles(result.knowledge.files);
          }
          if (result?.knowledgeIndexStatus) {
            setKnowledgeIndexStatus(
              normalizeKnowledgeIndexStatus(result.knowledgeIndexStatus)
            );
          }
        } catch (e) {
          console.error("File upload failed:", e);
        } finally {
          setUploadingFile(false);
        }
      }
    },
    [agentId, uploadingFile, knowledgeFiles.length]
  );

  const handleFileDelete = async (fileId) => {
    if (!agentId) return;
    try {
      const result = await useAgentKnowledgeStore
        .getState()
        .deleteKnowledgeFile(agentId, fileId);
      if (result?.knowledge?.files) {
        setKnowledgeFiles(result.knowledge.files);
      } else {
        setKnowledgeFiles((prev) => prev.filter((f) => f.fileId !== fileId));
      }
      if (result?.knowledgeIndexStatus) {
        setKnowledgeIndexStatus(
          normalizeKnowledgeIndexStatus(result.knowledgeIndexStatus)
        );
      }
    } catch (e) {
      console.error("File delete failed:", e);
    }
  };

  const handleSyncKnowledge = async () => {
    if (!agentId || indexingKnowledge) return;
    setIndexingKnowledge(true);
    let isPollingStopped = false;
    let pollingTimerId = null;
    const pollKnowledgeStatus = async () => {
      try {
        const status = await useAgentKnowledgeStore
          .getState()
          .fetchKnowledgeIndexStatus(agentId);
        if (!isPollingStopped && status) {
          setKnowledgeIndexStatus(normalizeKnowledgeIndexStatus(status));
        }
      } catch (error) {
        // Keep polling silent while indexing runs.
      }
    };
    try {
      await pollKnowledgeStatus();
      pollingTimerId = setInterval(() => {
        void pollKnowledgeStatus();
      }, 1000);
      const result = await useAgentKnowledgeStore
        .getState()
        .indexKnowledge(agentId);
      if (result?.knowledgeIndexStatus) {
        setKnowledgeIndexStatus(
          normalizeKnowledgeIndexStatus(result.knowledgeIndexStatus)
        );
      } else {
        await pollKnowledgeStatus();
      }
    } catch (e) {
      console.error("Indexing failed:", e);
    } finally {
      isPollingStopped = true;
      if (pollingTimerId) {
        clearInterval(pollingTimerId);
      }
      try {
        const status = await useAgentKnowledgeStore
          .getState()
          .fetchKnowledgeIndexStatus(agentId);
        if (status) {
          setKnowledgeIndexStatus(normalizeKnowledgeIndexStatus(status));
        }
      } catch (error) {
        // Ignore trailing status refresh failures.
      }
      setIndexingKnowledge(false);
    }
  };

  const handleDeleteKnowledgeIndex = async () => {
    if (!agentId || deletingKnowledgeIndex) return;
    setDeletingKnowledgeIndex(true);
    try {
      const result = await useAgentKnowledgeStore
        .getState()
        .deleteKnowledgeIndex(agentId);
      if (result?.knowledgeIndexStatus) {
        setKnowledgeIndexStatus(
          normalizeKnowledgeIndexStatus(result.knowledgeIndexStatus)
        );
      } else {
        const status = await useAgentKnowledgeStore
          .getState()
          .fetchKnowledgeIndexStatus(agentId);
        setKnowledgeIndexStatus(normalizeKnowledgeIndexStatus(status));
      }
    } catch (e) {
      console.error("Delete knowledge index failed:", e);
    } finally {
      setDeletingKnowledgeIndex(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const handleDelete = async () => {
    if (!agentId || deleteConfirmText !== name) return;
    setDeleting(true);
    const success = await useAgentStore.getState().deleteAgent(agentId);
    setDeleting(false);
    if (success) {
      onClose();
      navigate("/agents");
    }
  };

  const handleCreationPathSelect = (path) => {
    if (saving) return;
    setCreationPath(path);
    setCreationError("");
    setSaveResult(null);
    setSaveErrorMessage("");
    if (path === CREATE_PATH_MANUAL) {
      setActiveTab("general");
    }
  };

  const resetCreationPathSelection = () => {
    if (saving) return;
    setCreationPath(null);
    setCreationError("");
    setCreationStage(CREATE_STAGE_IDLE);
    setSaveResult(null);
    setSaveErrorMessage("");
  };

  const handleSave = async () => {
    if (showCreationPathChooser) return;
    if (isAiCreatePath && !normalizedAiRequirements) {
      setCreationError(
        "Add your instructions and requirements so AI can configure this agent."
      );
      setSaveResult("error");
      setSaveErrorMessage(
        "Add your instructions and requirements so AI can configure this agent."
      );
      return;
    }

    let normalizedEnabledTools = enabledTools.filter(
      (toolName) => !INTERNAL_ONLY_TOOL_NAMES.has(toolName)
    );
    normalizedEnabledTools = ensureSendEmailEnabledForPolicy(
      normalizedEnabledTools,
      emailResponsePolicyPreset
    );
    normalizedEnabledTools = ensureSelfConfigurationToolsForMode(
      normalizedEnabledTools,
      selfConfigurationMode
    );
    normalizedEnabledTools = ensureTaskQueuePlannerToolsPaired(
      normalizedEnabledTools
    );
    const normalizedSendSlackWebhookUrls = parseSlackWebhookUrls(
      sendSlackWebhookUrlsInput
    );
    const slackWebhookValidationMessage = normalizedEnabledTools.includes(
      SEND_SLACK_COMPONENT_TOOL_NAME
    )
      ? buildSlackWebhookValidationError(normalizedSendSlackWebhookUrls)
      : "";
    if (slackWebhookValidationMessage) {
      setSendSlackWebhookValidationError(slackWebhookValidationMessage);
      setRunWorkflowValidationError("");
      setSaveResult("error");
      setSaveErrorMessage(slackWebhookValidationMessage);
      setActiveTab("tools");
      return;
    }
    setSendSlackWebhookValidationError("");

    const normalizedRunWorkflowConfigs = (runWorkflowConfigs || [])
      .filter((wf) => String(wf?.workflowId || "").trim().length > 0)
      .map((wf) => {
        const workflowId = String(wf.workflowId || "").trim();
        return {
          workflowId,
          workflowName:
            String(wf.workflowName || workflowId).trim() || workflowId,
          workflowDescription: String(wf.workflowDescription || "").trim(),
          published: wf.published === true,
        };
      });
    const requiresRunWorkflowConfig =
      normalizedEnabledTools.includes("run_workflow");
    if (
      workflowScopeMode !== "ALL" &&
      requiresRunWorkflowConfig &&
      normalizedRunWorkflowConfigs.length === 0
    ) {
      const message =
        "Select at least one workflow when run_workflow is enabled.";
      setRunWorkflowValidationError(message);
      setSaveResult("error");
      setSaveErrorMessage(message);
      setActiveTab("tools");
      return;
    }
    setRunWorkflowValidationError("");

    const normalizedConfiguredTables =
      normalizeConfiguredTables(configuredTables);
    const normalizedConfiguredAgents =
      normalizeConfiguredAgents(configuredAgents);
    const normalizedKnowledgeTables = normalizeKnowledgeTables(knowledgeTables);
    const requiresConfiguredTables = normalizedEnabledTools.some((toolName) =>
      tableScopedToolNames.has(toolName)
    );
    if (
      tableScopeMode !== "ALL" &&
      requiresConfiguredTables &&
      normalizedConfiguredTables.length === 0
    ) {
      const message =
        "Select at least one table when table tools that require table IDs are enabled.";
      setConfiguredTablesValidationError(message);
      setSaveResult("error");
      setSaveErrorMessage(message);
      setActiveTab("tools");
      return;
    }
    setConfiguredTablesValidationError("");
    const normalizedReviewEscalationPolicy = reviewEscalationPolicy.trim();
    const normalizedReviewTaskAssigneeUserId = reviewTaskAssigneeUserId.trim();
    const normalizedBlockedWhileReviewMessage =
      customBlockedWhileReviewMessageEnabled
        ? blockedWhileReviewMessage.trim()
        : "";
    const effectiveEmailNotificationEnabled =
      reviewTaskEmailNotificationEnabled &&
      !!normalizedReviewTaskAssigneeUserId;
    const normalizedReviewTaskEmailNotifyUserId =
      effectiveEmailNotificationEnabled
        ? normalizedReviewTaskAssigneeUserId
        : "";
    // review_task config (escalation policy, assignee) is optional at save time —
    // the agent specifies the assignee in tool args at call time
    setSaving(true);
    setCreationError("");
    setSaveErrorMessage("");
    setSaveResult(null);
    const parsedNotifyEmails = completionNotifyEmails
      .split(",")
      .map((s) => s.trim())
      .filter((s) => s.length > 0);

    const normalizedExternalAccessPolicy =
      buildExternalAccessPolicy(externalAccessPolicy);
    const data = {
      name,
      description,
      systemPrompt,
      model,
      maxIterations,
      selfConfigurationMode,
      enabledTools: normalizedEnabledTools,
      toolInstructions: Object.fromEntries(
        Object.entries(toolInstructions).filter(
          ([, v]) => typeof v === "string" && v.trim().length > 0
        )
      ),
      sendSlackWebhookUrls: normalizedSendSlackWebhookUrls,
      runWorkflowConfigs: normalizedRunWorkflowConfigs,
      configuredTables: normalizedConfiguredTables,
      configuredAgents: normalizedConfiguredAgents,
      workflowScopeMode,
      tableScopeMode,
      agentScopeMode,
      fileScopeMode,
      artifactScopeMode,
      completionNotificationType,
      completionNotifyEmails: parsedNotifyEmails,
      completionSlackWebhookUrl,
      completionWebhookUrl,
      knowledge: {
        retrievalMode: knowledgeRetrievalMode,
        files: knowledgeFiles,
        tables: normalizedKnowledgeTables,
        manualContext: knowledgeManualContext,
      },
      allowAllSenders,
      acceptEmailAttachments,
      senderWhitelist: senderWhitelist
        .split("\n")
        .map((s) => s.trim())
        .filter((s) => s.length > 0),
      emailResponsePolicyPreset,
      emailResponsePolicyCustomPrompt,
      externalAccessPolicy: normalizedExternalAccessPolicy,
      externalChatAccessMode: normalizedExternalAccessPolicy.mode,
      externalChatMaxSessionsPerIdentity: parseOptionalPositiveInt(
        externalChatMaxSessionsPerIdentity
      ),
      externalChatMaxSessionsTotal: parseOptionalPositiveInt(
        externalChatMaxSessionsTotal
      ),
      externalChatRateLimitMessages:
        parseOptionalPositiveInt(externalChatRateLimitMessages) ?? 0,
      externalChatRateLimitPeriodSeconds: (() => {
        const periodValue = parseOptionalPositiveInt(
          externalChatRateLimitPeriodValue
        );
        return periodValue
          ? periodValue * periodUnitToSeconds(externalChatRateLimitPeriodUnit)
          : 0;
      })(),
      externalChatRateLimitExceededMessage:
        externalChatRateLimitExceededMessage.trim(),
      reviewEscalationPolicy: normalizedReviewEscalationPolicy,
      blockedWhileReviewMessage: normalizedBlockedWhileReviewMessage,
      autoReplyOnBlockedIngress,
      reviewTaskAssigneeUserId: normalizedReviewTaskAssigneeUserId,
      reviewTaskEmailNotificationEnabled: effectiveEmailNotificationEnabled,
      reviewTaskEmailNotifyUserId: normalizedReviewTaskEmailNotifyUserId,
      examplePrompts: normalizeExamplePrompts(examplePrompts),
      chatUiPolicy: {
        showSystemPromptInChat,
        showToolCallsAndOutputsInChat,
        showInterAgentMessagesInChat,
        showWorkflowProvenanceInChat,
        showSchedulingActivityInChat,
        showSessionOperationalMetaInChat,
        showSupervisorControlMessagesInChat,
        enableHumanTakeoverMode,
        showSharedSessionsSidebar,
      },
    };
    const showResult = (result, message = "") => {
      setSaveResult(result);
      if (result === "error") {
        setSaveErrorMessage(
          message || "Save failed. Check required tool settings and try again."
        );
      } else {
        setSaveErrorMessage("");
      }
      setTimeout(() => setSaveResult(null), result === "error" ? 5000 : 2000);
    };
    const showFailureResult = (error, fallbackMessage) =>
      showResult("error", extractSaveErrorMessage(error, fallbackMessage));

    try {
      if (isCreateMode) {
        if (isAiCreatePath) {
          const aiCreateData = {
            ...data,
            name: AI_AGENT_DEFAULT_NAME,
            description:
              data.description?.trim() || AI_AGENT_DEFAULT_DESCRIPTION,
            systemPrompt:
              data.systemPrompt?.trim() || AI_AGENT_BOOTSTRAP_SYSTEM_PROMPT,
            selfConfigurationMode: SELF_CONFIGURATION_MODE_AWARENESS_AND_EDIT,
            enabledTools: ensureSelfConfigurationToolsForMode(
              data.enabledTools,
              SELF_CONFIGURATION_MODE_AWARENESS_AND_EDIT
            ),
          };

          setCreationStage(CREATE_STAGE_CREATING_AGENT);
          const created = await useAgentStore
            .getState()
            .createAgent(aiCreateData);
          if (!created?.agentId) {
            setSaving(false);
            setCreationStage(CREATE_STAGE_IDLE);
            const message = extractSaveErrorMessage(
              null,
              useAgentStore.getState().error ||
                "We couldn't create the agent. Please try again."
            );
            setCreationError(message);
            showResult("error", message);
            return;
          }

          setCreationStage(CREATE_STAGE_STARTING_CHAT);
          const kickoffMessage =
            buildSelfConfigurationKickoffMessage(normalizedAiRequirements) ||
            normalizedAiRequirements;
          const started = await useAgentStore
            .getState()
            .startChat(created.agentId, kickoffMessage);

          setSaving(false);
          setCreationStage(CREATE_STAGE_IDLE);
          if (created?.knowledgeIndexStatus) {
            setKnowledgeIndexStatus(
              normalizeKnowledgeIndexStatus(created.knowledgeIndexStatus)
            );
          }
          if (started?.chatSessionId) {
            if (onCreated) {
              onCreated({
                ...created,
                chatSessionId: started.chatSessionId,
              });
            }
            showResult("success");
          } else {
            const message =
              "Agent created, but we couldn't start the first chat automatically. Opening the agent so you can continue manually.";
            setCreationError(message);
            showResult("error", message);
            setTimeout(() => {
              if (onCreated) onCreated(created);
            }, 900);
          }
          return;
        }

        const created = await useAgentStore.getState().createAgent(data);
        setSaving(false);
        if (created) {
          if (created.inboxAddress) setInboxAddress(created.inboxAddress);
          if (created.knowledgeIndexStatus) {
            setKnowledgeIndexStatus(
              normalizeKnowledgeIndexStatus(created.knowledgeIndexStatus)
            );
          }
          if (onCreated) onCreated(created);
          showResult("success");
        } else {
          showFailureResult(
            null,
            useAgentStore.getState().error ||
              "We couldn't create the agent. Please try again."
          );
        }
      } else {
        const updated = await useAgentStore
          .getState()
          .updateAgent(agentId, data);
        setSaving(false);
        if (updated) {
          if (updated.inboxAddress) setInboxAddress(updated.inboxAddress);
          if (updated.knowledgeIndexStatus) {
            setKnowledgeIndexStatus(
              normalizeKnowledgeIndexStatus(updated.knowledgeIndexStatus)
            );
          }
          if (onSaved) onSaved(updated);
          showResult("success");
        } else {
          showFailureResult(
            null,
            useAgentStore.getState().error ||
              "We couldn't save changes. Check required tool settings and try again."
          );
        }
      }
    } catch (error) {
      setSaving(false);
      setCreationStage(CREATE_STAGE_IDLE);
      const message = extractSaveErrorMessage(
        error,
        useAgentStore.getState().error
      );
      if (isAiCreatePath) {
        setCreationError(message);
      }
      showResult("error", message);
    }
  };

  const renderCreationPathChooser = () => (
    <AgentConfigCreationPathChooser
      onSelectManual={() => handleCreationPathSelect(CREATE_PATH_MANUAL)}
      onSelectAi={() => handleCreationPathSelect(CREATE_PATH_AI)}
      saving={saving}
    />
  );

  const renderAiCreationPanel = () => {
    const stageLabel =
      creationStage === CREATE_STAGE_CREATING_AGENT
        ? "Creating agent..."
        : creationStage === CREATE_STAGE_STARTING_CHAT
        ? "Starting first chat..."
        : "";

    return (
      <AgentConfigAiCreationPanel
        aiRequirements={aiRequirements}
        setAiRequirements={setAiRequirements}
        creationError={creationError}
        setCreationError={setCreationError}
        saving={saving}
        stageLabel={stageLabel}
      />
    );
  };

  const renderGeneralTab = () => (
    <>
      {isSystemAgent && (
        <Box
          sx={{
            mb: 2.5,
            p: 1.5,
            borderRadius: "8px",
            backgroundColor: "action.hover",
            border: "1px solid",
            borderColor: "divider",
          }}
        >
          <Typography sx={{ fontSize: "12px", color: "text.secondary" }}>
            This is a system agent. Only the model can be changed.
          </Typography>
        </Box>
      )}
      {/* Details */}
      <Typography
        sx={{ fontSize: "14px", fontWeight: 600, color: "text.primary", mb: 2 }}
      >
        Details
      </Typography>
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6}>
          <Typography className="org-field-label">Name</Typography>
          <TextField
            className="org-styled-textfield"
            fullWidth
            size="small"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="My Agent"
            disabled={isSystemAgent}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography className="org-field-label">Description</Typography>
          <TextField
            className="org-styled-textfield"
            fullWidth
            size="small"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="A brief description of what this agent does"
            disabled={isSystemAgent}
          />
        </Grid>
        {!isSystemAgent && (
          <Grid item xs={12}>
            <Typography className="org-field-label">
              Self Configuration
            </Typography>
            <Box
              sx={{
                border: "1px solid",
                borderColor: "divider",
                borderRadius: "8px",
                p: 2,
                backgroundColor: "action.hover",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "stretch",
                  gap: 0.5,
                }}
              >
                <FormControlLabel
                  sx={{ m: 0, width: "100%", display: "flex" }}
                  control={
                    <Checkbox
                      checked={isSelfConfigurationAwarenessEnabled(
                        selfConfigurationMode
                      )}
                      onChange={(e) => {
                        const enabled = e.target.checked;
                        const nextMode = !enabled
                          ? SELF_CONFIGURATION_MODE_OFF
                          : isSelfConfigurationEditEnabled(
                              selfConfigurationMode
                            )
                          ? SELF_CONFIGURATION_MODE_AWARENESS_AND_EDIT
                          : SELF_CONFIGURATION_MODE_AWARENESS_ONLY;
                        setSelfConfigurationMode(nextMode);
                        setEnabledTools((prev) =>
                          ensureSelfConfigurationToolsForMode(
                            ensureTaskQueuePlannerToolsPaired(prev),
                            nextMode
                          )
                        );
                      }}
                    />
                  }
                  label={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <VisibilityOutlinedIcon
                        sx={{ fontSize: 16, color: "text.secondary" }}
                      />
                      <Typography
                        sx={{ fontSize: "13px", color: "text.primary" }}
                      >
                        Enable self-configuration awareness
                      </Typography>
                    </Box>
                  }
                />
                <FormControlLabel
                  sx={{ m: 0, width: "100%", display: "flex" }}
                  control={
                    <Checkbox
                      checked={isSelfConfigurationEditEnabled(
                        selfConfigurationMode
                      )}
                      disabled={
                        !isSelfConfigurationAwarenessEnabled(
                          selfConfigurationMode
                        )
                      }
                      onChange={(e) => {
                        const nextMode = e.target.checked
                          ? SELF_CONFIGURATION_MODE_AWARENESS_AND_EDIT
                          : SELF_CONFIGURATION_MODE_AWARENESS_ONLY;
                        setSelfConfigurationMode(nextMode);
                        setEnabledTools((prev) =>
                          ensureSelfConfigurationToolsForMode(
                            ensureTaskQueuePlannerToolsPaired(prev),
                            nextMode
                          )
                        );
                      }}
                    />
                  }
                  label={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <EditOutlinedIcon
                        sx={{ fontSize: 16, color: "text.secondary" }}
                      />
                      <Typography
                        sx={{ fontSize: "13px", color: "text.primary" }}
                      >
                        Allow agent to edit its own config
                      </Typography>
                    </Box>
                  }
                />
              </Box>
              <Typography
                sx={{ fontSize: "11px", color: "text.secondary", mt: 1 }}
              >
                {getSelfConfigurationModeHelpText(selfConfigurationMode)}
              </Typography>
            </Box>
          </Grid>
        )}
        {!isSystemAgent && (
          <Grid item xs={12}>
            <Typography className="org-field-label">System Prompt</Typography>
            <TipTapEditor
              value={systemPrompt}
              onChange={setSystemPrompt}
              placeholder="You are a helpful agent that..."
              markdown
              showToolbar={true}
              minHeight="160px"
              triggerChars={["$", "@"]}
              availablePlaceholders={globalVarPlaceholders}
              agentMentions={allAgents.map((a) => ({
                key: a.name,
                type: "agent",
                id: a.agentId,
                description: a.description,
              }))}
            />
            <Typography
              sx={{ fontSize: "11px", color: "text.disabled", mt: 0.5 }}
            >
              Instructions that define the agent's behavior. Type @ to reference
              other agents, $ for placeholders.
            </Typography>
          </Grid>
        )}
      </Grid>

      <Divider sx={{ borderColor: "divider", my: 2.5 }} />

      {/* Model & Limits */}
      <Typography
        sx={{ fontSize: "14px", fontWeight: 600, color: "text.primary", mb: 2 }}
      >
        Model & Limits
      </Typography>
      <Grid container spacing={3}>
        <Grid item xs={12} sm={8}>
          <Typography className="org-field-label">Model</Typography>
          <ModelSelectField
            className="org-styled-textfield"
            fullWidth
            size="small"
            value={model}
            onChange={setModel}
          />
        </Grid>
        {!isSystemAgent && (
          <Grid item xs={12} sm={4}>
            <Typography className="org-field-label">Max Iterations</Typography>
            <TextField
              className="org-styled-textfield"
              type="number"
              fullWidth
              size="small"
              value={maxIterations}
              onChange={(e) => {
                const val = parseInt(e.target.value, 10);
                if (!isNaN(val) && val >= 1 && val <= 200)
                  setMaxIterations(val);
              }}
              inputProps={{ min: 1, max: 200 }}
            />
            <Typography
              sx={{ fontSize: "11px", color: "text.disabled", mt: 0.5 }}
            >
              Per execution (1-200).
            </Typography>
          </Grid>
        )}
      </Grid>

      {!isSystemAgent && (
        <>
          <Divider sx={{ borderColor: "divider", my: 2.5 }} />

          {/* Completion Notification */}
          <Typography
            sx={{
              fontSize: "14px",
              fontWeight: 600,
              color: "text.primary",
              mb: 0.5,
            }}
          >
            Completion Notification
          </Typography>
          <Typography sx={{ fontSize: "13px", color: "text.disabled", mb: 2 }}>
            Notify when the agent has finished responding.
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography className="org-field-label">
                Notification Channel
              </Typography>
              <TextField
                className="org-styled-textfield"
                select
                fullWidth
                size="small"
                value={completionNotificationType}
                onChange={(e) => setCompletionNotificationType(e.target.value)}
              >
                {COMPLETION_NOTIFICATION_TYPES.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {completionNotificationType === "EMAIL" && (
              <Grid item xs={12}>
                <Typography className="org-field-label">
                  Email Recipients
                </Typography>
                <TextField
                  className="org-styled-textfield"
                  fullWidth
                  size="small"
                  value={completionNotifyEmails}
                  onChange={(e) => setCompletionNotifyEmails(e.target.value)}
                  placeholder="name@example.com, ops@example.com"
                />
                <Typography
                  sx={{ fontSize: "11px", color: "text.disabled", mt: 0.5 }}
                >
                  Comma-separated email addresses.
                </Typography>
              </Grid>
            )}

            {completionNotificationType === "SLACK" && (
              <Grid item xs={12}>
                <Typography className="org-field-label">
                  Slack Webhook URL
                </Typography>
                <TextField
                  className="org-styled-textfield"
                  fullWidth
                  size="small"
                  value={completionSlackWebhookUrl}
                  onChange={(e) => setCompletionSlackWebhookUrl(e.target.value)}
                  placeholder="https://hooks.slack.com/services/..."
                />
              </Grid>
            )}

            {completionNotificationType === "WEBHOOK" && (
              <Grid item xs={12}>
                <Typography className="org-field-label">
                  Webhook URL (POST)
                </Typography>
                <TextField
                  className="org-styled-textfield"
                  fullWidth
                  size="small"
                  value={completionWebhookUrl}
                  onChange={(e) => setCompletionWebhookUrl(e.target.value)}
                  placeholder="https://your-server.com/webhooks/agent"
                />
                <Typography
                  sx={{ fontSize: "11px", color: "text.disabled", mt: 0.5 }}
                >
                  Receives a JSON payload with session details and direct links
                  to the chat.
                </Typography>
                <Box
                  sx={{
                    mt: 1.5,
                    border: 1,
                    borderColor: "divider",
                    borderRadius: "8px",
                    overflow: "hidden",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "11px",
                      color: "text.secondary",
                      px: 1.5,
                      py: 0.75,
                      backgroundColor: "action.hover",
                      borderBottom: 1,
                      borderColor: "divider",
                    }}
                  >
                    Payload Preview
                  </Typography>
                  <Box
                    component="pre"
                    sx={{
                      m: 0,
                      p: 1.5,
                      fontSize: "11px",
                      lineHeight: 1.45,
                      overflowX: "auto",
                      backgroundColor: "background.paper",
                      color: "text.secondary",
                    }}
                  >
                    {JSON.stringify(webhookPayloadPreview, null, 2)}
                  </Box>
                </Box>
                <Typography
                  sx={{ fontSize: "11px", color: "text.disabled", mt: 0.75 }}
                >
                  `workflowSession` link is included when the agent run is tied
                  to a workflow session.
                </Typography>
              </Grid>
            )}
          </Grid>
        </>
      )}
    </>
  );

  const renderChatUxTab = () => (
    <>
      <Typography
        sx={{
          fontSize: "14px",
          fontWeight: 600,
          color: "text.primary",
          mb: 0.5,
        }}
      >
        Chat Transcript Visibility
      </Typography>
      <Typography sx={{ fontSize: "13px", color: "text.disabled", mb: 2 }}>
        Control what end users can see in the chat transcript. These settings do
        not change what is included in the AI context/loop.
      </Typography>

      <Typography
        sx={{
          fontSize: "14px",
          fontWeight: 600,
          color: "text.primary",
          mb: 0.5,
        }}
      >
        Example Prompts
      </Typography>
      <Typography sx={{ fontSize: "13px", color: "text.disabled", mb: 1.5 }}>
        Shown as clickable cards under the new-chat textfield. Clicking a card
        inserts the prompt without sending it.
      </Typography>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        {examplePrompts.length === 0 && (
          <Typography sx={{ fontSize: "13px", color: "text.secondary" }}>
            No example prompts configured.
          </Typography>
        )}
        {examplePrompts.map((example, index) => (
          <Box
            key={`${index}-${example.prompt}`}
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "180px 1fr auto" },
              gap: 1,
              alignItems: "flex-start",
            }}
          >
            <TextField
              size="small"
              label="Card title"
              value={example.title || ""}
              disabled={isSystemAgent}
              onChange={(event) =>
                setExamplePrompts((prev) =>
                  prev.map((item, itemIndex) =>
                    itemIndex === index
                      ? { ...item, title: event.target.value }
                      : item
                  )
                )
              }
            />
            <TextField
              size="small"
              label="Prompt inserted into chat"
              value={example.prompt || ""}
              disabled={isSystemAgent}
              multiline
              minRows={2}
              onChange={(event) =>
                setExamplePrompts((prev) =>
                  prev.map((item, itemIndex) =>
                    itemIndex === index
                      ? { ...item, prompt: event.target.value }
                      : item
                  )
                )
              }
            />
            {!isSystemAgent && (
              <IconButton
                size="small"
                onClick={() =>
                  setExamplePrompts((prev) =>
                    prev.filter((_, itemIndex) => itemIndex !== index)
                  )
                }
                sx={{ mt: 0.5, color: "text.secondary" }}
              >
                <DeleteOutlineIcon sx={{ fontSize: 18 }} />
              </IconButton>
            )}
          </Box>
        ))}
        {!isSystemAgent && examplePrompts.length < 6 && (
          <Button
            variant="outlined"
            onClick={() =>
              setExamplePrompts((prev) => [...prev, { title: "", prompt: "" }])
            }
            sx={{ alignSelf: "flex-start", textTransform: "none" }}
          >
            Add example prompt
          </Button>
        )}
      </Box>

      <Divider sx={{ borderColor: "divider", my: 2.5 }} />

      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        <ToggleRow
          checked={showSystemPromptInChat}
          onChange={setShowSystemPromptInChat}
          title="Show system prompt block in chat UI"
          description="Off hides the expandable system prompt section from responders."
        />
        <ToggleRow
          checked={showToolCallsAndOutputsInChat}
          onChange={setShowToolCallsAndOutputsInChat}
          title="Show tool calls and tool outputs in chat UI"
          description="Off hides internal tool activity cards while preserving form interactions."
        />
        <ToggleRow
          checked={showInterAgentMessagesInChat}
          onChange={setShowInterAgentMessagesInChat}
          title="Show inter-agent routing messages"
          description="Off hides agent-to-agent message cards and routing chatter from responders."
        />
        <ToggleRow
          checked={showWorkflowProvenanceInChat}
          onChange={setShowWorkflowProvenanceInChat}
          title="Show workflow provenance cards"
          description="Off hides workflow/source context wrappers and keeps the transcript focused on plain conversation content."
        />
        <ToggleRow
          checked={showSchedulingActivityInChat}
          onChange={setShowSchedulingActivityInChat}
          title="Show scheduling activity"
          description="Off hides schedule-message tool actions and timing activity cards."
        />
        <ToggleRow
          checked={showSessionOperationalMetaInChat}
          onChange={setShowSessionOperationalMetaInChat}
          title="Show session operational metadata"
          description="Off hides session email/thread metadata and other operational UI details."
        />
        <ToggleRow
          checked={showSupervisorControlMessagesInChat}
          onChange={setShowSupervisorControlMessagesInChat}
          title="Show supervisor control messages"
          description="Off hides supervisor review/takeover control messages from responder transcripts."
        />
        <ToggleRow
          checked={enableHumanTakeoverMode}
          onChange={setEnableHumanTakeoverMode}
          title="Enable human takeover mode"
          description="Lets operators pause AI responses per session in internal chat UI so humans can converse directly."
        />
      </Box>
    </>
  );

  const renderDangerTab = () => (
    <>
      <Box
        sx={{
          border: 1,
          borderColor: "error.light",
          borderRadius: "8px",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            px: 2,
            py: 1.5,
            backgroundColor: (theme) => alpha(theme.palette.error.main, 0.05),
            borderBottom: 1,
            borderColor: "error.light",
          }}
        >
          <WarningAmberIcon sx={{ fontSize: 18, color: "error.main" }} />
          <Typography
            sx={{ fontSize: "14px", fontWeight: 600, color: "error.dark" }}
          >
            Delete this agent
          </Typography>
        </Box>
        <Box sx={{ px: 2, py: 2 }}>
          <Typography
            sx={{
              fontSize: "13px",
              color: "text.secondary",
              mb: 2,
              lineHeight: 1.5,
            }}
          >
            This will permanently delete the agent <strong>{name}</strong>, all
            of its chat sessions, knowledge files, configuration, and email
            inbox. This action cannot be undone.
          </Typography>
          <Typography sx={{ fontSize: "13px", color: "text.secondary", mb: 1 }}>
            To confirm, type <strong>{name}</strong> below:
          </Typography>
          <TextField
            fullWidth
            size="small"
            value={deleteConfirmText}
            onChange={(e) => setDeleteConfirmText(e.target.value)}
            placeholder={name}
            sx={{
              mb: 2,
              "& .MuiOutlinedInput-root": {
                fontSize: "13px",
                borderRadius: "6px",
                "& fieldset": { borderColor: "error.light" },
                "&:hover fieldset": { borderColor: "error.light" },
                "&.Mui-focused fieldset": { borderColor: "error.main" },
              },
            }}
          />
          <Button
            fullWidth
            onClick={handleDelete}
            disabled={deleteConfirmText !== name || deleting}
            sx={{
              textTransform: "none",
              fontSize: "13px",
              fontWeight: 600,
              borderRadius: "6px",
              py: 1,
              backgroundColor:
                deleteConfirmText === name
                  ? "error.main"
                  : (theme) => alpha(theme.palette.error.main, 0.2),
              color:
                deleteConfirmText === name
                  ? "error.contrastText"
                  : "error.light",
              border: 1,
              borderColor:
                deleteConfirmText === name
                  ? "error.main"
                  : (theme) => alpha(theme.palette.error.main, 0.2),
              "&:hover": {
                backgroundColor: "error.dark",
                borderColor: "error.dark",
              },
              "&.Mui-disabled": {
                backgroundColor: (theme) =>
                  alpha(theme.palette.error.main, 0.2),
                color: "error.light",
                borderColor: (theme) => alpha(theme.palette.error.main, 0.2),
              },
            }}
          >
            {deleting ? "Deleting..." : "I understand, delete this agent"}
          </Button>
        </Box>
      </Box>
    </>
  );

  const renderToolsTab = () => {
    const tableScopeEnabled = enabledTools.some((toolName) =>
      tableScopedToolNames.has(toolName)
    );
    const sendSlackComponentEnabled = enabledTools.includes(
      SEND_SLACK_COMPONENT_TOOL_NAME
    );
    const normalizedSendSlackWebhookUrls = parseSlackWebhookUrls(
      sendSlackWebhookUrlsInput
    );
    const liveSlackWebhookValidationError = sendSlackComponentEnabled
      ? buildSlackWebhookValidationError(normalizedSendSlackWebhookUrls)
      : "";
    const slackWebhookValidationMessage =
      sendSlackWebhookValidationError || liveSlackWebhookValidationError;
    const workflowById = new Map();
    workflowOptions.forEach((option) => {
      if (option?.workflowId) workflowById.set(option.workflowId, option);
    });
    runWorkflowConfigs.forEach((option) => {
      if (option?.workflowId && !workflowById.has(option.workflowId)) {
        workflowById.set(option.workflowId, option);
      }
    });
    const selectableWorkflowOptions = Array.from(workflowById.values());
    const selectedWorkflowValues = runWorkflowConfigs
      .map((cfg) => workflowById.get(cfg.workflowId) || cfg)
      .filter((cfg) => !!cfg?.workflowId);
    const tableById = new Map();
    const normalizedConfiguredTableScope =
      normalizeConfiguredTables(configuredTables);
    databaseList.forEach((option) => {
      const tableId = String(
        option?.databaseId || option?.tableId || ""
      ).trim();
      if (!tableId) return;
      tableById.set(tableId, {
        tableId,
        tableName: option?.name || option?.tableName || tableId,
      });
    });
    normalizedConfiguredTableScope.forEach((option) => {
      if (option?.tableId && !tableById.has(option.tableId)) {
        tableById.set(option.tableId, option);
      }
    });
    const selectableTableOptions = Array.from(tableById.values());
    const selectedTableValues = normalizedConfiguredTableScope
      .map((cfg) => tableById.get(cfg.tableId) || cfg)
      .filter((cfg) => !!cfg?.tableId);
    const agentById = new Map();
    agentOptions.forEach((option) => {
      if (option?.agentId) agentById.set(option.agentId, option);
    });
    configuredAgents.forEach((option) => {
      if (option?.agentId && !agentById.has(option.agentId)) {
        agentById.set(option.agentId, option);
      }
    });
    const selectableAgentOptions = Array.from(agentById.values()).filter(
      (agent) => agent?.agentId !== agentId
    );
    const selectedAgentValues = normalizeConfiguredAgents(configuredAgents)
      .map((cfg) => agentById.get(cfg.agentId) || cfg)
      .filter((cfg) => !!cfg?.agentId);
    const filteredToolCategories = filterToolCategories(
      toolCategories,
      toolSearchQuery
    );
    const hasToolSearchQuery = toolSearchQuery.trim().length > 0;
    const matchingToolCount = filteredToolCategories.reduce(
      (total, category) => total + category.tools.length,
      0
    );
    // Any tool the agent has previously enabled that no longer shows up in
    // the tenant's current catalog — typically a Custom Function that was
    // soft-deleted or had its agent-tool exposure flipped off. The registered
    // stub adapter still responds with "disabled" at invocation time, but
    // surfacing it here lets the user proactively drop it from enabledTools.
    const catalogToolNames = new Set();
    toolCategories.forEach((cat) => {
      (cat?.tools || []).forEach((tool) => {
        if (tool?.name) catalogToolNames.add(tool.name);
      });
    });
    const staleEnabledTools =
      !loadingToolCatalog && toolCategories.length > 0
        ? enabledTools.filter(
            (toolName) => toolName && !catalogToolNames.has(toolName)
          )
        : [];
    const reviewTaskEnabled = enabledTools.includes("review_task");
    const renderReviewTaskControls = () => (
      <Box
        sx={{
          mt: 1.25,
          ml: 1.5,
          pl: 1.5,
          pr: 0.5,
          borderLeft: 2,
          borderColor: "divider",
        }}
      >
        <Typography className="org-field-label" sx={{ mb: 0.5 }}>
          Create Task Controls
        </Typography>
        <Typography sx={{ fontSize: "11px", color: "text.secondary", mb: 1 }}>
          Create Tasks always go to Task Inbox. Email is optional additional
          notification and can only target active users in this account.
        </Typography>
        {ResolvedPeoplePicker ? (
          <ResolvedPeoplePicker
            label="Assigned user"
            placeholder="Search people..."
            items={reviewTaskEligibleUsers}
            selectedIds={
              reviewTaskAssigneeUserId.trim() ? [reviewTaskAssigneeUserId] : []
            }
            loading={loadingReviewTaskEligibleUsers}
            onToggle={(userName) =>
              setReviewTaskAssigneeUserId(
                reviewTaskAssigneeUserId === userName ? "" : userName
              )
            }
            getId={(user) => user.userName}
            getName={(user) => user.displayName || user.userName}
            getDetail={(user) => user.email || ""}
            getAvatarLabel={(user) =>
              user.displayName || user.userName || "User"
            }
            emptyStateLabel="No active users found in this account."
          />
        ) : (
          <TextField
            className="org-styled-textfield"
            select
            fullWidth
            size="small"
            value={reviewTaskAssigneeUserId}
            onChange={(e) => setReviewTaskAssigneeUserId(e.target.value)}
            disabled={loadingReviewTaskEligibleUsers}
            error={!reviewTaskAssigneeUserId.trim() && saving}
            helperText={
              loadingReviewTaskEligibleUsers
                ? "Loading active users..."
                : "Select one active user as the default assignee."
            }
          >
            <MenuItem value="">Select assignee...</MenuItem>
            {(Array.isArray(reviewTaskEligibleUsers)
              ? reviewTaskEligibleUsers
              : []
            ).map((user) => (
              <MenuItem key={user.userName} value={user.userName}>
                {user.displayName || user.userName}
                {user.email ? ` (${user.email})` : ""}
              </MenuItem>
            ))}
          </TextField>
        )}
        <Typography
          sx={{
            fontSize: "11px",
            color:
              !reviewTaskAssigneeUserId.trim() && saving
                ? "error.main"
                : "text.disabled",
            mt: 0.5,
            mb: 1,
          }}
        >
          {!reviewTaskAssigneeUserId.trim() && saving
            ? "Required. Select an internal account user."
            : "Select exactly one internal assignee."}
        </Typography>
        <Divider sx={{ borderColor: "divider", my: 1.25 }} />
        <Typography className="org-field-label" sx={{ mb: 0.5 }}>
          Escalation rules
        </Typography>
        <Typography sx={{ fontSize: "11px", color: "text.secondary", mb: 1 }}>
          Define when the agent should escalate to human review and what details
          must be included for the assignee.
        </Typography>
        <TextField
          className="org-styled-textfield"
          fullWidth
          multiline
          minRows={4}
          size="small"
          value={reviewEscalationPolicy}
          onChange={(e) => setReviewEscalationPolicy(e.target.value)}
          placeholder="Describe when the AI should create a Create Task and what context should be included."
          helperText="Optional. Guides the agent on when to escalate to human review."
          sx={{ mb: 1 }}
        />
        <Divider sx={{ borderColor: "divider", my: 1.25 }} />
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={reviewTaskEmailNotificationEnabled}
              onChange={(e) =>
                setReviewTaskEmailNotificationEnabled(e.target.checked)
              }
            />
          }
          label="Also send task link by email"
          sx={{ color: "text.secondary", mb: 0.5 }}
        />
        <Typography
          sx={{
            fontSize: "11px",
            color:
              reviewTaskEmailNotificationEnabled &&
              !reviewTaskAssigneeUserId.trim() &&
              saving
                ? "error.main"
                : "text.disabled",
            mt: 0.5,
            mb: 1,
          }}
        >
          {reviewTaskEmailNotificationEnabled &&
          !reviewTaskAssigneeUserId.trim() &&
          saving
            ? "Select an assigned user to enable email notifications."
            : "Optional extra channel. Emails go to the assigned user above."}
        </Typography>
        <Divider sx={{ borderColor: "divider", my: 1.25 }} />
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={customBlockedWhileReviewMessageEnabled}
              onChange={(e) =>
                setCustomBlockedWhileReviewMessageEnabled(e.target.checked)
              }
            />
          }
          label="Customize blocked message (chat + blocked email auto-replies)"
          sx={{
            color: "text.secondary",
            mb: customBlockedWhileReviewMessageEnabled ? 0.25 : 0,
          }}
        />
        <Typography
          sx={{
            fontSize: "11px",
            color: "text.secondary",
            mb: customBlockedWhileReviewMessageEnabled ? 0.75 : 0,
          }}
        >
          This message is shown in blocked chat sessions and is also used for
          blocked email auto-replies when enabled below.
        </Typography>
        {customBlockedWhileReviewMessageEnabled && (
          <TextField
            className="org-styled-textfield"
            fullWidth
            multiline
            minRows={2}
            size="small"
            value={blockedWhileReviewMessage}
            onChange={(e) => setBlockedWhileReviewMessage(e.target.value)}
            placeholder="Custom blocked message shown while the session is under review."
            helperText={
              autoReplyOnBlockedIngress
                ? "Used in blocked chat sessions and blocked email auto-replies."
                : "Used in blocked chat sessions. Email auto-replies are disabled below."
            }
            sx={{ mb: 1 }}
          />
        )}
        <Divider sx={{ borderColor: "divider", my: 1.25 }} />
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={autoReplyOnBlockedIngress}
              onChange={(e) => setAutoReplyOnBlockedIngress(e.target.checked)}
            />
          }
          label="Auto-reply on blocked non-UI ingress (email/inter-agent/scheduled)"
          sx={{ color: "text.secondary" }}
        />
        <Typography sx={{ fontSize: "11px", color: "text.disabled", mt: 0.5 }}>
          Controls blocked email auto-replies and uses the blocked message
          above.
        </Typography>
      </Box>
    );

    const renderTableScopeConfig = () => (
      <Box>
        <Typography className="org-field-label" sx={{ mb: 0.5 }}>
          Table Tool Scope
        </Typography>
        <TextField
          className="org-styled-textfield"
          select
          fullWidth
          size="small"
          value={tableScopeMode}
          onChange={(e) => setTableScopeMode(e.target.value)}
          helperText="Choose whether table tools can use all current and future tables, or only selected tables."
          sx={{ mb: 1 }}
        >
          <MenuItem value="ALL">All current and future tables</MenuItem>
          <MenuItem value="SELECTED">Selected tables only</MenuItem>
        </TextField>
        {tableScopeMode === "ALL" ? (
          <Typography sx={{ fontSize: "12px", color: "text.secondary" }}>
            All current and future tables are included. The agent should use
            list_tables and get_table to discover exact table IDs.
          </Typography>
        ) : (
          <Autocomplete
            multiple
            options={selectableTableOptions}
            value={selectedTableValues}
            loading={loadingDatabases}
            getOptionLabel={(option) =>
              option.tableName || option.tableId || ""
            }
            isOptionEqualToValue={(option, value) =>
              option.tableId === value.tableId
            }
            onChange={(_, newValue) => {
              setConfiguredTables(normalizeConfiguredTables(newValue));
              setConfiguredTablesValidationError("");
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                className="org-styled-textfield"
                variant="outlined"
                size="small"
                placeholder="Select tables for table tools..."
                error={Boolean(configuredTablesValidationError)}
                helperText={
                  configuredTablesValidationError ||
                  "Shared across all table tools. Scopes what tables this agent can read/write."
                }
              />
            )}
            renderOption={(props, option) => (
              <li {...props} key={option.tableId}>
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  <Typography
                    sx={{ fontSize: "13px", color: "text.secondary" }}
                  >
                    {option.tableName || option.tableId}
                  </Typography>
                  <Typography sx={{ fontSize: "11px", color: "text.disabled" }}>
                    {option.tableId}
                  </Typography>
                </Box>
              </li>
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  {...getTagProps({ index })}
                  key={option.tableId}
                  label={option.tableName || option.tableId}
                  size="small"
                  sx={{ fontSize: "12px" }}
                />
              ))
            }
            size="small"
          />
        )}
      </Box>
    );

    const renderRunWorkflowConfig = () => (
      <Box>
        <Typography className="org-field-label" sx={{ mb: 0.5 }}>
          Run Workflow Tool Scope
        </Typography>
        <TextField
          className="org-styled-textfield"
          select
          fullWidth
          size="small"
          value={workflowScopeMode}
          onChange={(e) => setWorkflowScopeMode(e.target.value)}
          helperText="Choose whether run_workflow can use all current and future workflows, or only selected workflows."
          sx={{ mb: 1 }}
        >
          <MenuItem value="ALL">All current and future workflows</MenuItem>
          <MenuItem value="SELECTED">Selected workflows only</MenuItem>
        </TextField>
        {workflowScopeMode === "ALL" ? (
          <Typography sx={{ fontSize: "12px", color: "text.secondary" }}>
            All current and future workflows are included. The agent should use
            list_workflows and read_workflow to discover exact workflow IDs.
          </Typography>
        ) : (
          <Autocomplete
            multiple
            options={selectableWorkflowOptions}
            value={selectedWorkflowValues}
            loading={loadingWorkflowOptions}
            getOptionLabel={(option) =>
              option.workflowName || option.workflowId || ""
            }
            isOptionEqualToValue={(option, value) =>
              option.workflowId === value.workflowId
            }
            onChange={(_, newValue) => {
              setRunWorkflowConfigs(
                newValue
                  .filter((wf) => !!wf?.workflowId)
                  .map((wf) => ({
                    workflowId: wf.workflowId,
                    workflowName: wf.workflowName || wf.workflowId,
                    workflowDescription: wf.workflowDescription || "",
                    published: wf.published === true,
                  }))
              );
              setRunWorkflowValidationError("");
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                className="org-styled-textfield"
                variant="outlined"
                size="small"
                placeholder="Select workflows for run_workflow..."
                error={Boolean(runWorkflowValidationError)}
                helperText={
                  runWorkflowValidationError ||
                  "Choose one or more workflows this agent is allowed to run."
                }
              />
            )}
            renderOption={(props, option) => (
              <li {...props} key={option.workflowId}>
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  <Typography
                    sx={{ fontSize: "13px", color: "text.secondary" }}
                  >
                    {option.workflowName || option.workflowId}
                  </Typography>
                  <Typography sx={{ fontSize: "11px", color: "text.disabled" }}>
                    {option.workflowId}
                  </Typography>
                </Box>
              </li>
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  {...getTagProps({ index })}
                  key={option.workflowId}
                  label={option.workflowName || option.workflowId}
                  size="small"
                  sx={{ fontSize: "12px" }}
                />
              ))
            }
            size="small"
          />
        )}
      </Box>
    );

    const renderAgentScopeConfig = () => (
      <Box sx={{ mb: 2 }}>
        <Typography className="org-field-label" sx={{ mb: 0.5 }}>
          Agent Interaction Scope
        </Typography>
        <TextField
          className="org-styled-textfield"
          select
          fullWidth
          size="small"
          value={agentScopeMode}
          onChange={(e) => setAgentScopeMode(e.target.value)}
          helperText="Choose which agents can be discovered or messaged by agent tools."
          sx={{ mb: 1 }}
        >
          <MenuItem value="ALL">All current and future agents</MenuItem>
          <MenuItem value="SELECTED">Selected agents only</MenuItem>
        </TextField>
        {agentScopeMode === "ALL" ? (
          <Typography sx={{ fontSize: "12px", color: "text.secondary" }}>
            All current and future customer agents are included.
          </Typography>
        ) : (
          <Autocomplete
            multiple
            options={selectableAgentOptions}
            value={selectedAgentValues}
            loading={loadingAgentOptions}
            getOptionLabel={(option) => option.name || option.agentId || ""}
            isOptionEqualToValue={(option, value) =>
              option.agentId === value.agentId
            }
            onChange={(_, newValue) => {
              setConfiguredAgents(normalizeConfiguredAgents(newValue));
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                className="org-styled-textfield"
                variant="outlined"
                size="small"
                placeholder="Select agents..."
                helperText="Scopes list_agents, list_agent_sessions, and send_message."
              />
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  {...getTagProps({ index })}
                  key={option.agentId}
                  label={option.name || option.agentId}
                  size="small"
                  sx={{ fontSize: "12px" }}
                />
              ))
            }
            size="small"
          />
        )}
      </Box>
    );

    const renderSlackWebhooksConfig = () => (
      <Box>
        <Typography className="org-field-label" sx={{ mb: 0.5 }}>
          Allowed Slack Webhooks
        </Typography>
        <TextField
          className="org-styled-textfield"
          fullWidth
          multiline
          minRows={3}
          size="small"
          value={sendSlackWebhookUrlsInput}
          onChange={(e) => {
            setSendSlackWebhookUrlsInput(e.target.value);
            if (sendSlackWebhookValidationError) {
              setSendSlackWebhookValidationError("");
            }
          }}
          placeholder="https://hooks.slack.com/services/...\nhttps://hooks.slack.com/services/..."
          error={Boolean(slackWebhookValidationMessage)}
          helperText={
            slackWebhookValidationMessage ||
            "Enter one Slack incoming webhook URL per line (or comma-separated)."
          }
        />
      </Box>
    );

    const renderToolConfig = (tool) => {
      if (!tool) return null;
      if (!enabledTools.includes(tool.name)) return null;
      if (tool.name === "run_workflow") {
        return renderRunWorkflowConfig();
      }
      if (tool.name === SEND_SLACK_COMPONENT_TOOL_NAME) {
        return renderSlackWebhooksConfig();
      }
      if (tool.name === "review_task") {
        return (
          <Box>
            <Typography className="org-field-label" sx={{ mb: 0.5 }}>
              Create Task Controls
            </Typography>
            {renderReviewTaskControls()}
          </Box>
        );
      }
      return null;
    };

    const renderCategoryConfig = (category) => {
      if (!category) return null;
      if (category.title === "Tables" && tableScopeEnabled) {
        return renderTableScopeConfig();
      }
      return null;
    };

    return (
      <>
        <Typography
          sx={{
            fontSize: "14px",
            fontWeight: 600,
            color: "text.primary",
            mb: 0.5,
          }}
        >
          Tools
        </Typography>
        <Typography sx={{ fontSize: "13px", color: "text.disabled", mb: 2 }}>
          Select which tools this agent can use during execution.
        </Typography>

        {enabledTools.some((toolName) =>
          ["list_agents", "list_agent_sessions", "send_message"].includes(
            toolName
          )
        ) && renderAgentScopeConfig()}

        {hasToolSearchQuery && (
          <Typography
            sx={{ fontSize: "11px", color: "text.secondary", mb: 1.5 }}
          >
            {matchingToolCount} matching tool
            {matchingToolCount === 1 ? "" : "s"} for "{toolSearchQuery.trim()}".
          </Typography>
        )}
        {staleEnabledTools.length > 0 && (
          <Alert
            severity="warning"
            action={
              <Button
                color="inherit"
                size="small"
                onClick={() =>
                  setEnabledTools((prev) =>
                    prev.filter(
                      (toolName) => !staleEnabledTools.includes(toolName)
                    )
                  )
                }
              >
                Remove
              </Button>
            }
            sx={{ mb: 1.5 }}
          >
            <Typography sx={{ fontSize: "12px" }}>
              {staleEnabledTools.length === 1
                ? "1 enabled tool is no longer available:"
                : `${staleEnabledTools.length} enabled tools are no longer available:`}{" "}
              <strong>{staleEnabledTools.join(", ")}</strong>. They were likely
              deleted or unexposed by the tenant.
            </Typography>
          </Alert>
        )}
        <AgentToolLoadout
          toolCategories={toolCategories}
          inventoryCategories={filteredToolCategories}
          enabledTools={enabledTools}
          onToggleTool={toggleTool}
          toolInstructions={toolInstructions}
          onUpdateToolInstruction={(toolName, value) => {
            setToolInstructions((prev) => {
              if (value === undefined) {
                const next = { ...prev };
                delete next[toolName];
                return next;
              }
              return { ...prev, [toolName]: value };
            });
          }}
          toolSearchQuery={toolSearchQuery}
          setToolSearchQuery={setToolSearchQuery}
          loadingToolCatalog={loadingToolCatalog}
          renderToolConfig={renderToolConfig}
          renderCategoryConfig={renderCategoryConfig}
        />
      </>
    );
  };

  const handleCopyInbox = () => {
    navigator.clipboard.writeText(inboxAddress);
  };

  const handleAddWhitelistEntry = () => {
    const entry = newWhitelistEntry.trim();
    if (!entry) return;
    const current = senderWhitelist.trim();
    setSenderWhitelist(current ? current + "\n" + entry : entry);
    setNewWhitelistEntry("");
  };

  const handleRemoveWhitelistEntry = (entryToRemove) => {
    const entries = senderWhitelist
      .split("\n")
      .map((s) => s.trim())
      .filter((s) => s.length > 0 && s !== entryToRemove);
    setSenderWhitelist(entries.join("\n"));
  };

  const handleCreatePublicShareLink = async () => {
    if (!agentId || creatingPublicShareLink || revokingPublicShareLink) return;
    setCreatingPublicShareLink(true);
    setPublicShareLinkError("");
    try {
      const normalizedExternalAccessPolicy =
        buildExternalAccessPolicy(externalAccessPolicy);
      const payload = {
        accessPolicy: normalizedExternalAccessPolicy,
        accessMode: normalizedExternalAccessPolicy.mode,
        allowedEmails: normalizedExternalAccessPolicy.allowedEmails,
        maxSessionsPerIdentity: parseOptionalPositiveInt(
          externalChatMaxSessionsPerIdentity
        ),
        maxSessionsTotal: parseOptionalPositiveInt(
          externalChatMaxSessionsTotal
        ),
        rateLimitMessages: parseOptionalPositiveInt(
          externalChatRateLimitMessages
        ),
        rateLimitPeriodSeconds: (() => {
          const periodValue = parseOptionalPositiveInt(
            externalChatRateLimitPeriodValue
          );
          return periodValue
            ? periodValue * periodUnitToSeconds(externalChatRateLimitPeriodUnit)
            : null;
        })(),
        rateLimitExceededMessage:
          externalChatRateLimitExceededMessage.trim() || null,
      };
      if (enablePublicShareLinkExpiry && publicShareLinkExpiresAt) {
        const expiresDate = new Date(publicShareLinkExpiresAt);
        if (!Number.isNaN(expiresDate.getTime())) {
          payload.expiresAt = expiresDate.toISOString();
        }
      }
      const created = await usePublicChatStore
        .getState()
        .createPublicChatShareLink(agentId, payload);
      if (created?.url) {
        setPublicShareLink(created);
      } else {
        setPublicShareLinkError(
          usePublicChatStore.getState().error ||
            "Failed to create public chat share link."
        );
      }
    } catch (error) {
      setPublicShareLinkError(
        error?.message || "Failed to create public chat share link."
      );
    } finally {
      setCreatingPublicShareLink(false);
    }
  };

  const handleRevokePublicShareLink = async () => {
    if (!agentId || !publicShareLink?.shareId || revokingPublicShareLink)
      return;
    setRevokingPublicShareLink(true);
    setPublicShareLinkError("");
    try {
      const revoked = await usePublicChatStore
        .getState()
        .revokePublicChatShareLink(agentId, publicShareLink.shareId, {});
      if (revoked) {
        setPublicShareLink(null);
      } else {
        setPublicShareLinkError(
          usePublicChatStore.getState().error ||
            "Failed to revoke public chat share link."
        );
      }
    } catch (error) {
      setPublicShareLinkError(
        error?.message || "Failed to revoke public chat share link."
      );
    } finally {
      setRevokingPublicShareLink(false);
    }
  };

  const handleCopyPublicShareLink = () => {
    if (!publicShareLink?.url) return;
    navigator.clipboard.writeText(publicShareLink.url);
  };

  const renderAccessTab = () => {
    const normalizedExternalAccessPolicy =
      buildExternalAccessPolicy(externalAccessPolicy);
    const selectedAccessMode =
      EXTERNAL_CHAT_ACCESS_MODE_OPTIONS.find(
        (option) => option.value === normalizedExternalAccessPolicy.mode
      ) || EXTERNAL_CHAT_ACCESS_MODE_OPTIONS[0];
    const isDisabledMode =
      normalizedExternalAccessPolicy.mode ===
      EXTERNAL_CHAT_ACCESS_MODE_DISABLED;
    const currentLinkUrl =
      publicShareLink?.url ||
      (publicShareLink?.tokenValue
        ? config.buildAgentChatUrl(publicShareLink.tokenValue)
        : "");
    const perIdentityLimitOptions = buildNumericLimitSelectOptions(
      EXTERNAL_CHAT_MAX_SESSIONS_PER_IDENTITY_PRESETS,
      externalChatMaxSessionsPerIdentity
    );
    const totalLimitOptions = buildNumericLimitSelectOptions(
      EXTERNAL_CHAT_MAX_SESSIONS_TOTAL_PRESETS,
      externalChatMaxSessionsTotal
    );
    const ratePeriodUnitLabel =
      EXTERNAL_CHAT_RATE_LIMIT_PERIOD_UNITS.find(
        (option) => option.value === externalChatRateLimitPeriodUnit
      )?.label || externalChatRateLimitPeriodUnit;
    const ratePeriodDisplayLabel =
      String(externalChatRateLimitPeriodValue).trim() === "1"
        ? ratePeriodUnitLabel.replace(/s$/, "")
        : ratePeriodUnitLabel;

    return (
      <>
        <Typography
          sx={{
            fontSize: "14px",
            fontWeight: 600,
            color: "text.primary",
            mb: 0.5,
          }}
        >
          Public Chat Access
        </Typography>
        <Typography sx={{ fontSize: "13px", color: "text.disabled", mb: 2 }}>
          Configure who can use this agent's public chat link and how usage is
          limited.
        </Typography>

        <PublicAccessPolicyEditor
          accessPolicy={normalizedExternalAccessPolicy}
          onChange={setExternalAccessPolicy}
          users={reviewTaskEligibleUsers}
          usersLoading={loadingReviewTaskEligibleUsers}
        />
        <Typography sx={{ fontSize: "12px", color: "text.disabled", mb: 2 }}>
          {selectedAccessMode.description}
        </Typography>
        {Array.isArray(selectedAccessMode.detailLines) &&
          selectedAccessMode.detailLines.length > 0 && (
            <Box
              sx={{
                mb: 2,
                p: 1.25,
                border: "1px solid",
                borderColor: "divider",
                borderRadius: "10px",
                backgroundColor: "action.hover",
              }}
            >
              {selectedAccessMode.detailLines.map((line) => (
                <Typography
                  key={line}
                  sx={{
                    fontSize: "12px",
                    color: "text.secondary",
                    lineHeight: 1.6,
                  }}
                >
                  {`\u2022 ${line}`}
                </Typography>
              ))}
            </Box>
          )}

        <Box sx={{ mb: 2 }}>
          <ToggleRow
            checked={showSharedSessionsSidebar}
            onChange={setShowSharedSessionsSidebar}
            title="Show sessions sidebar on shared links"
            description="Turn off to hide the left session list. When hidden, the shared page shows a New chat button in the top-right header."
          />
        </Box>

        <Box sx={{ mb: 2, display: "flex", flexDirection: "column", gap: 1.5 }}>
          <Box>
            <Typography className="org-field-label">
              Max sessions per identity
            </Typography>
            <TextField
              className="org-styled-textfield"
              select
              fullWidth
              size="small"
              value={externalChatMaxSessionsPerIdentity}
              onChange={(event) =>
                setExternalChatMaxSessionsPerIdentity(event.target.value)
              }
              SelectProps={{
                displayEmpty: true,
                renderValue: (value) =>
                  getLimitOptionLabel(perIdentityLimitOptions, value),
              }}
            >
              {perIdentityLimitOptions.map((option) => (
                <MenuItem key={option.value || "none"} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
            <Typography
              sx={{ fontSize: "12px", color: "text.secondary", mt: 0.5 }}
            >
              Caps how many sessions one identity can start from this link.
              Identity is derived from anonymous browser ID, verified email, or
              authenticated account depending on access mode.
            </Typography>
          </Box>

          <Box>
            <Typography className="org-field-label">
              Max sessions total
            </Typography>
            <TextField
              className="org-styled-textfield"
              select
              fullWidth
              size="small"
              value={externalChatMaxSessionsTotal}
              onChange={(event) =>
                setExternalChatMaxSessionsTotal(event.target.value)
              }
              SelectProps={{
                displayEmpty: true,
                renderValue: (value) =>
                  getLimitOptionLabel(totalLimitOptions, value),
              }}
            >
              {totalLimitOptions.map((option) => (
                <MenuItem key={option.value || "none"} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
            <Typography
              sx={{ fontSize: "12px", color: "text.secondary", mt: 0.5 }}
            >
              Caps total sessions started across all users for this share link.
              To start this count fresh, generate a new link.
            </Typography>
          </Box>

          <Box>
            <Typography className="org-field-label">
              User message rate limit
            </Typography>
            <Box
              sx={{
                display: "flex",
                gap: 1,
                alignItems: "center",
                flexWrap: "wrap",
              }}
            >
              <TextField
                className="org-styled-textfield"
                size="small"
                sx={{ width: 110 }}
                type="number"
                inputProps={{ min: 1, step: 1 }}
                placeholder="No limit"
                value={externalChatRateLimitMessages}
                onChange={(event) =>
                  setExternalChatRateLimitMessages(event.target.value)
                }
              />
              <Typography sx={{ fontSize: "13px", color: "text.secondary" }}>
                messages per
              </Typography>
              <TextField
                className="org-styled-textfield"
                size="small"
                sx={{ width: 80 }}
                type="number"
                inputProps={{ min: 1, step: 1 }}
                value={externalChatRateLimitPeriodValue}
                onChange={(event) =>
                  setExternalChatRateLimitPeriodValue(event.target.value)
                }
              />
              <TextField
                className="org-styled-textfield"
                select
                size="small"
                sx={{ width: 120 }}
                value={externalChatRateLimitPeriodUnit}
                onChange={(event) =>
                  setExternalChatRateLimitPeriodUnit(event.target.value)
                }
              >
                {EXTERNAL_CHAT_RATE_LIMIT_PERIOD_UNITS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
            <Typography
              sx={{ fontSize: "12px", color: "text.secondary", mt: 0.5 }}
            >
              {`Caps how many user messages a single identity can send per ${
                externalChatRateLimitPeriodValue || "?"
              } ${ratePeriodDisplayLabel}. Counts both new sessions and follow-up messages. Leave the message count blank for no limit.`}
            </Typography>
            <Box sx={{ mt: 1.5 }}>
              <Typography className="org-field-label">
                Rate limit message
              </Typography>
              <TextField
                className="org-styled-textfield"
                fullWidth
                size="small"
                multiline
                minRows={2}
                placeholder={EXTERNAL_CHAT_RATE_LIMIT_DEFAULT_EXCEEDED_MESSAGE}
                value={externalChatRateLimitExceededMessage}
                onChange={(event) =>
                  setExternalChatRateLimitExceededMessage(event.target.value)
                }
              />
              <Typography
                sx={{ fontSize: "12px", color: "text.secondary", mt: 0.5 }}
              >
                Shown in chat when the rate limit is hit. Leave blank to use the
                default.
              </Typography>
            </Box>
          </Box>
        </Box>

        <Divider sx={{ borderColor: "divider", my: 2.5 }} />

        <Typography
          sx={{
            fontSize: "14px",
            fontWeight: 600,
            color: "text.primary",
            mb: 0.5,
          }}
        >
          Share Link
        </Typography>
        <Typography sx={{ fontSize: "13px", color: "text.disabled", mb: 1.5 }}>
          Generate or rotate a public chat link for this agent.
        </Typography>

        {!agentId ? (
          <Typography sx={{ fontSize: "12px", color: "text.disabled" }}>
            Create this agent first, then generate a public chat link.
          </Typography>
        ) : (
          <>
            <Box sx={{ mb: 1.5 }}>
              <ToggleRow
                checked={enablePublicShareLinkExpiry}
                onChange={(nextChecked) => {
                  setEnablePublicShareLinkExpiry(nextChecked);
                  if (!nextChecked) {
                    setPublicShareLinkExpiresAt("");
                  }
                }}
                title="Enable link expiry"
                description="Turn on to set an expiry date/time for newly generated links."
              />
            </Box>

            {enablePublicShareLinkExpiry && (
              <>
                <Typography className="org-field-label">
                  Link expiry (optional)
                </Typography>
                <TextField
                  className="org-styled-textfield"
                  fullWidth
                  type="datetime-local"
                  size="small"
                  disabled={isDisabledMode}
                  value={publicShareLinkExpiresAt}
                  onChange={(event) =>
                    setPublicShareLinkExpiresAt(event.target.value)
                  }
                  sx={{ mb: 1.5 }}
                  helperText={
                    isDisabledMode
                      ? "Enable an access mode first to set link expiry."
                      : ""
                  }
                  InputLabelProps={{ shrink: true }}
                />
              </>
            )}

            <Box sx={{ display: "flex", gap: 1, mb: 1.25 }}>
              <Button
                variant="contained"
                size="small"
                startIcon={<LinkOutlinedIcon sx={{ fontSize: 16 }} />}
                disabled={
                  creatingPublicShareLink ||
                  revokingPublicShareLink ||
                  isDisabledMode
                }
                onClick={handleCreatePublicShareLink}
                sx={{ textTransform: "none" }}
              >
                {publicShareLink ? "Rotate link" : "Create link"}
              </Button>
              {publicShareLink?.shareId && (
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  disabled={revokingPublicShareLink || creatingPublicShareLink}
                  onClick={handleRevokePublicShareLink}
                  sx={{ textTransform: "none" }}
                >
                  Revoke
                </Button>
              )}
            </Box>

            {isDisabledMode && (
              <Typography
                sx={{ fontSize: "12px", color: "text.disabled", mb: 1 }}
              >
                Set an access mode above to enable public links.
              </Typography>
            )}

            {currentLinkUrl && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <TextField
                  className="org-styled-textfield"
                  fullWidth
                  size="small"
                  value={currentLinkUrl}
                  InputProps={{ readOnly: true }}
                />
                <IconButton
                  size="small"
                  onClick={handleCopyPublicShareLink}
                  title="Copy public chat link"
                >
                  <ContentCopyIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Box>
            )}
          </>
        )}

        {publicShareLinkError && (
          <Typography sx={{ fontSize: "12px", color: "error.dark", mt: 1 }}>
            {publicShareLinkError}
          </Typography>
        )}
      </>
    );
  };

  const renderEmailTab = () => {
    const whitelistEntries = senderWhitelist
      .split("\n")
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
    const selectedEmailPreset =
      EMAIL_RESPONSE_POLICY_PRESETS.find(
        (preset) => preset.value === emailResponsePolicyPreset
      ) || EMAIL_RESPONSE_POLICY_PRESETS[0];

    return (
      <>
        <Typography
          sx={{
            fontSize: "14px",
            fontWeight: 600,
            color: "text.primary",
            mb: 0.5,
          }}
        >
          Email Inbox
        </Typography>
        <Typography sx={{ fontSize: "13px", color: "text.disabled", mb: 2 }}>
          This agent has a dedicated email inbox. Incoming emails that pass the
          whitelist will start or continue a chat session.
        </Typography>

        <Box sx={{ mb: 3 }}>
          <Typography className="org-field-label">Inbox Address</Typography>
          {inboxAddress ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <TextField
                className="org-styled-textfield"
                fullWidth
                size="small"
                value={inboxAddress}
                InputProps={{ readOnly: true }}
              />
              <IconButton
                size="small"
                onClick={handleCopyInbox}
                title="Copy to clipboard"
              >
                <ContentCopyIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Box>
          ) : (
            <Typography
              sx={{
                fontSize: "13px",
                color: "text.disabled",
                fontStyle: "italic",
              }}
            >
              Inbox address will be assigned when the agent is created.
            </Typography>
          )}
        </Box>

        <Divider sx={{ borderColor: "divider", my: 2.5 }} />

        <Typography
          sx={{
            fontSize: "14px",
            fontWeight: 600,
            color: "text.primary",
            mb: 0.5,
          }}
        >
          Sender Whitelist
        </Typography>
        <Typography sx={{ fontSize: "13px", color: "text.disabled", mb: 2 }}>
          Control who can email this agent. Add email addresses or domain
          patterns (e.g. @company.com).
        </Typography>

        <Box sx={{ mb: 2 }}>
          <ToggleRow
            checked={allowAllSenders}
            onChange={setAllowAllSenders}
            title="Allow all senders"
            description="When disabled, only addresses in the whitelist below can email this agent."
          />
        </Box>

        {!allowAllSenders && (
          <Box>
            <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
              <TextField
                className="org-styled-textfield"
                fullWidth
                size="small"
                value={newWhitelistEntry}
                onChange={(e) => setNewWhitelistEntry(e.target.value)}
                placeholder="user@example.com or @example.com"
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleAddWhitelistEntry();
                  }
                }}
              />
              <Button
                variant="outlined"
                size="small"
                onClick={handleAddWhitelistEntry}
                disabled={!newWhitelistEntry.trim()}
                sx={{ minWidth: 60, textTransform: "none", fontSize: "13px" }}
              >
                Add
              </Button>
            </Box>

            {whitelistEntries.length > 0 ? (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                {whitelistEntries.map((entry, idx) => (
                  <Chip
                    key={idx}
                    label={entry}
                    size="small"
                    onDelete={() => handleRemoveWhitelistEntry(entry)}
                    sx={{ fontSize: "12px" }}
                  />
                ))}
              </Box>
            ) : (
              <Typography
                sx={{
                  fontSize: "13px",
                  color: "text.disabled",
                  fontStyle: "italic",
                }}
              >
                No senders whitelisted. The agent will not process any incoming
                emails.
              </Typography>
            )}
          </Box>
        )}

        <Divider sx={{ borderColor: "divider", my: 2.5 }} />

        <Typography
          sx={{
            fontSize: "14px",
            fontWeight: 600,
            color: "text.primary",
            mb: 0.5,
          }}
        >
          Email Response Rules
        </Typography>
        <Typography sx={{ fontSize: "13px", color: "text.disabled", mb: 2 }}>
          Controls how the AI should handle outbound responses when the user
          message comes from email.
        </Typography>

        <Typography className="org-field-label">Preset</Typography>
        <TextField
          className="org-styled-textfield"
          select
          fullWidth
          size="small"
          value={emailResponsePolicyPreset}
          onChange={(e) => {
            const nextPreset = e.target.value;
            setEmailResponsePolicyPreset(nextPreset);
            setEnabledTools((prev) =>
              ensureTaskQueuePlannerToolsPaired(
                ensureSendEmailEnabledForPolicy(prev, nextPreset)
              )
            );
          }}
          sx={{ mb: 0.75 }}
        >
          {EMAIL_RESPONSE_POLICY_PRESETS.map((preset) => (
            <MenuItem key={preset.value} value={preset.value}>
              {preset.label}
            </MenuItem>
          ))}
        </TextField>
        <Typography sx={{ fontSize: "12px", color: "text.disabled", mb: 2 }}>
          {selectedEmailPreset?.description}
        </Typography>

        <Typography className="org-field-label">Custom Instructions</Typography>
        <TextField
          className="org-styled-textfield"
          fullWidth
          multiline
          minRows={4}
          value={emailResponsePolicyCustomPrompt}
          onChange={(e) => setEmailResponsePolicyCustomPrompt(e.target.value)}
          placeholder="Optional instructions for email handling, tone, or escalation steps."
        />
        <Typography sx={{ fontSize: "11px", color: "text.disabled", mt: 0.75 }}>
          This text is injected into the system prompt only when handling
          inbound email user messages.
        </Typography>

        <Divider sx={{ borderColor: "divider", my: 2.5 }} />

        <Typography
          sx={{
            fontSize: "14px",
            fontWeight: 600,
            color: "text.primary",
            mb: 0.5,
          }}
        >
          Email Attachments
        </Typography>
        <Typography sx={{ fontSize: "13px", color: "text.disabled", mb: 2 }}>
          When enabled, file attachments on incoming emails will be uploaded to
          this agent's file library and made available to the agent.
        </Typography>

        <Box sx={{ mb: 2 }}>
          <ToggleRow
            checked={acceptEmailAttachments}
            onChange={setAcceptEmailAttachments}
            title="Accept email attachments"
            description="Upload inbound email attachments to the agent's file library so it can read, edit, and delete them."
          />
        </Box>
      </>
    );
  };

  const renderKnowledgeTab = () => {
    const normalizedKnowledgeStatus =
      normalizeKnowledgeIndexStatus(knowledgeIndexStatus);
    const normalizedKnowledgeTables = normalizeKnowledgeTables(knowledgeTables);
    const knowledgeTableById = new Map();
    databaseList.forEach((option) => {
      const databaseId = String(
        option?.databaseId || option?.tableId || ""
      ).trim();
      if (!databaseId) return;
      knowledgeTableById.set(databaseId, {
        databaseId,
        tableName: option?.name || option?.tableName || databaseId,
      });
    });
    normalizedKnowledgeTables.forEach((table) => {
      if (table?.databaseId && !knowledgeTableById.has(table.databaseId)) {
        knowledgeTableById.set(table.databaseId, table);
      }
    });
    const selectableKnowledgeTableOptions = Array.from(
      knowledgeTableById.values()
    );
    const selectedKnowledgeTableValues = normalizedKnowledgeTables
      .map((table) => knowledgeTableById.get(table.databaseId) || table)
      .filter((table) => !!table?.databaseId);
    const knowledgeIndexState = normalizedKnowledgeStatus.state;
    const hasKnowledgeConfigured =
      knowledgeFiles.length > 0 ||
      normalizedKnowledgeTables.length > 0 ||
      Boolean((knowledgeManualContext || "").trim());
    const statusLabel =
      KNOWLEDGE_INDEX_STATE_COPY[knowledgeIndexState] ||
      KNOWLEDGE_INDEX_STATE_COPY.NO_KNOWLEDGE;
    const statusChipColor =
      knowledgeIndexState === "READY"
        ? "success.main"
        : knowledgeIndexState === "FAILED"
        ? "error.main"
        : knowledgeIndexState === "INDEXING"
        ? "info.main"
        : knowledgeIndexState === "STALE"
        ? "warning.dark"
        : "text.secondary";
    const phaseLabelMap = {
      ASSEMBLING: "Assembling sources",
      INDEXING: "Building vector index",
      COMPLETED: "Completed",
      FAILED: "Failed",
      NO_KNOWLEDGE: "No knowledge content",
    };
    const phaseLabel =
      phaseLabelMap[normalizedKnowledgeStatus.phase] ||
      normalizedKnowledgeStatus.phase ||
      null;
    const parsedProgressPercent = Number(
      normalizedKnowledgeStatus.progressPercent
    );
    const progressPercent = Number.isFinite(parsedProgressPercent)
      ? Math.max(0, Math.min(100, Math.round(parsedProgressPercent)))
      : null;
    const showProgressBar =
      knowledgeIndexState === "INDEXING" || indexingKnowledge;
    const parseOptionalCount = (value) => {
      if (value === null || value === undefined) {
        return null;
      }
      const normalized = String(value).trim();
      if (!normalized) {
        return null;
      }
      const parsed = Number(normalized);
      if (!Number.isFinite(parsed)) {
        return null;
      }
      return Math.max(0, Math.round(parsed));
    };
    const sourcesTotal = parseOptionalCount(
      normalizedKnowledgeStatus.sourcesTotal
    );
    const sourcesProcessed = parseOptionalCount(
      normalizedKnowledgeStatus.sourcesProcessed
    );
    const filesTotal = parseOptionalCount(normalizedKnowledgeStatus.filesTotal);
    const tablesTotal = parseOptionalCount(
      normalizedKnowledgeStatus.tablesTotal
    );
    const workloadSegments = [];
    if (Number.isFinite(filesTotal)) {
      workloadSegments.push(`${filesTotal} file${filesTotal === 1 ? "" : "s"}`);
    }
    if (Number.isFinite(tablesTotal)) {
      workloadSegments.push(
        `${tablesTotal} table${tablesTotal === 1 ? "" : "s"}`
      );
    }
    if (normalizedKnowledgeStatus.hasManualContext === true) {
      workloadSegments.push("manual context");
    }
    const workloadSummary =
      workloadSegments.length > 0
        ? `Workload: ${workloadSegments.join(" \u00b7 ")}`
        : null;

    return (
      <>
        <Typography
          sx={{
            fontSize: "14px",
            fontWeight: 600,
            color: "text.primary",
            mb: 0.5,
          }}
        >
          Knowledge
        </Typography>
        <Typography sx={{ fontSize: "13px", color: "text.disabled", mb: 2 }}>
          Configure the knowledge base the agent will use for all responses.
        </Typography>

        <Box
          sx={{
            mb: 2.5,
            p: 1.5,
            border: "1px solid",
            borderColor: "divider",
            borderRadius: "8px",
            backgroundColor: "action.hover",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
              mb: 1,
              flexWrap: "wrap",
            }}
          >
            <Typography className="org-field-label" sx={{ mb: 0 }}>
              Index Status
            </Typography>
            <Chip
              size="small"
              label={
                KNOWLEDGE_INDEX_STATE_LABELS[knowledgeIndexState] ||
                knowledgeIndexState
              }
              sx={{
                fontSize: "11px",
                fontWeight: 600,
                color: statusChipColor,
                borderColor: statusChipColor,
                backgroundColor: "background.paper",
                borderRadius: "4px",
                height: 22,
                "& .MuiChip-label": { px: 1 },
              }}
              variant="outlined"
            />
          </Box>
          <Typography
            sx={{ fontSize: "12px", color: "text.secondary", mb: 0.5 }}
          >
            {statusLabel}
          </Typography>
          {(phaseLabel || normalizedKnowledgeStatus.message) && (
            <Typography
              sx={{ fontSize: "11px", color: "text.secondary", mb: 0.5 }}
            >
              {[phaseLabel, normalizedKnowledgeStatus.message]
                .filter((segment) => !!segment)
                .join(" \u00b7 ")}
            </Typography>
          )}
          {showProgressBar && (
            <Box sx={{ mb: 0.75 }}>
              <LinearProgress
                variant={
                  Number.isFinite(progressPercent)
                    ? "determinate"
                    : "indeterminate"
                }
                value={
                  Number.isFinite(progressPercent) ? progressPercent : undefined
                }
                sx={{
                  height: 6,
                  borderRadius: 999,
                  backgroundColor: "divider",
                  "& .MuiLinearProgress-bar": {
                    borderRadius: 999,
                    backgroundColor: statusChipColor,
                  },
                }}
              />
              <Typography
                sx={{ fontSize: "11px", color: "text.secondary", mt: 0.5 }}
              >
                {Number.isFinite(progressPercent)
                  ? `Progress: ${progressPercent}%`
                  : "Progress: running..."}
                {Number.isFinite(sourcesProcessed) ||
                Number.isFinite(sourcesTotal)
                  ? ` \u00b7 Sources: ${
                      Number.isFinite(sourcesProcessed) ? sourcesProcessed : 0
                    }/${Number.isFinite(sourcesTotal) ? sourcesTotal : "?"}`
                  : ""}
              </Typography>
            </Box>
          )}
          {workloadSummary && (
            <Typography
              sx={{ fontSize: "11px", color: "text.secondary", mb: 0.5 }}
            >
              {workloadSummary}
            </Typography>
          )}
          {normalizedKnowledgeStatus.lastError && (
            <Typography sx={{ fontSize: "11px", color: "error.main", mb: 0.5 }}>
              {normalizedKnowledgeStatus.lastError}
            </Typography>
          )}
          {normalizedKnowledgeStatus.lastIndexedAt && (
            <Typography
              sx={{ fontSize: "11px", color: "text.disabled", mb: 0.75 }}
            >
              Last indexed:{" "}
              {new Date(
                normalizedKnowledgeStatus.lastIndexedAt
              ).toLocaleString()}
              {Number.isFinite(normalizedKnowledgeStatus.chunksIndexed)
                ? ` · ${normalizedKnowledgeStatus.chunksIndexed} chunks`
                : ""}
            </Typography>
          )}
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Button
              variant="outlined"
              size="small"
              onClick={handleSyncKnowledge}
              disabled={
                isCreateMode || indexingKnowledge || !hasKnowledgeConfigured
              }
              sx={{
                textTransform: "none",
                fontSize: "12px",
                fontWeight: 500,
                borderColor: "divider",
                color: "text.secondary",
                borderRadius: "6px",
                "&:hover": {
                  borderColor: "text.disabled",
                  backgroundColor: "action.hover",
                },
              }}
            >
              {indexingKnowledge ? "Syncing..." : "Sync now"}
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={handleDeleteKnowledgeIndex}
              disabled={
                isCreateMode ||
                deletingKnowledgeIndex ||
                knowledgeIndexState === "NO_KNOWLEDGE"
              }
              sx={{
                textTransform: "none",
                fontSize: "12px",
                fontWeight: 500,
                borderColor: "divider",
                color: "text.secondary",
                borderRadius: "6px",
                "&:hover": { borderColor: "error.main", color: "error.main" },
              }}
            >
              {deletingKnowledgeIndex ? "Deleting..." : "Delete index"}
            </Button>
          </Box>
          <Typography
            sx={{ fontSize: "11px", color: "text.disabled", mt: 0.75 }}
          >
            {isCreateMode
              ? "Save the agent first to enable index operations."
              : !hasKnowledgeConfigured
              ? "Add files, tables, or manual context, then save to build an index."
              : "Knowledge changes are synced automatically on save. Use Sync now to re-run indexing manually."}
          </Typography>
        </Box>

        <Box sx={{ mb: 2.5 }}>
          <Typography className="org-field-label">Retrieval Mode</Typography>
          <Chip
            size="small"
            label="RAG (fixed)"
            sx={{
              fontSize: "11px",
              fontWeight: 600,
              color: "info.main",
              borderColor: (theme) => alpha(theme.palette.info.main, 0.3),
              backgroundColor: (theme) => alpha(theme.palette.info.main, 0.08),
              borderRadius: "4px",
              height: 22,
              "& .MuiChip-label": { px: 1 },
            }}
            variant="outlined"
          />
          <Typography
            sx={{ fontSize: "11px", color: "text.disabled", mt: 0.5 }}
          >
            The agent now uses RAG retrieval for every run and continuation
            turn.
          </Typography>
        </Box>

        <Divider sx={{ borderColor: "divider", my: 2.5 }} />

        <Box sx={{ mb: 2.5 }}>
          <Typography className="org-field-label">Files</Typography>
          {isCreateMode && (
            <Typography
              sx={{ fontSize: "12px", color: "text.disabled", mb: 1 }}
            >
              Save the agent first to enable file uploads.
            </Typography>
          )}
          <Dropzone
            onDrop={handleFileDrop}
            accept={ACCEPTED_KNOWLEDGE_FORMATS}
            multiple
            maxFiles={MAX_KNOWLEDGE_FILES - knowledgeFiles.length}
            disabled={
              isCreateMode ||
              uploadingFile ||
              knowledgeFiles.length >= MAX_KNOWLEDGE_FILES
            }
          >
            {({ getRootProps, getInputProps, isDragActive }) => (
              <Box
                {...getRootProps()}
                sx={{
                  border: "1.5px dashed",
                  borderColor: isDragActive ? "text.primary" : "divider",
                  borderRadius: "8px",
                  p: 2.5,
                  textAlign: "center",
                  cursor:
                    isCreateMode ||
                    uploadingFile ||
                    knowledgeFiles.length >= MAX_KNOWLEDGE_FILES
                      ? "not-allowed"
                      : "pointer",
                  backgroundColor: isDragActive
                    ? "action.hover"
                    : "background.paper",
                  transition: "all 0.15s",
                  "&:hover": {
                    borderColor: "text.disabled",
                    backgroundColor: "action.hover",
                  },
                  opacity:
                    isCreateMode || knowledgeFiles.length >= MAX_KNOWLEDGE_FILES
                      ? 0.5
                      : 1,
                }}
              >
                <input {...getInputProps()} />
                {uploadingFile ? (
                  <CircularProgress
                    size={20}
                    sx={{ color: "text.secondary" }}
                  />
                ) : (
                  <>
                    <CloudUploadOutlinedIcon
                      sx={{ fontSize: 28, color: "text.disabled", mb: 0.5 }}
                    />
                    <Typography
                      sx={{ fontSize: "13px", color: "text.secondary" }}
                    >
                      Drag & drop or click to upload
                    </Typography>
                    <Typography
                      sx={{ fontSize: "11px", color: "text.disabled", mt: 0.5 }}
                    >
                      .csv, .json, .pdf, .xlsx, .xls, .txt, .md, .docx, .pptx —
                      Max {MAX_KNOWLEDGE_FILES} files
                    </Typography>
                  </>
                )}
              </Box>
            )}
          </Dropzone>
          {knowledgeFiles.length > 0 && (
            <Box sx={{ mt: 1.5 }}>
              {knowledgeFiles.map((file) => (
                <Box
                  key={file.fileId}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    py: 0.75,
                    px: 1,
                    borderRadius: "6px",
                    "&:hover": { backgroundColor: "action.hover" },
                  }}
                >
                  <DescriptionOutlinedIcon
                    sx={{ fontSize: 18, color: "text.disabled", flexShrink: 0 }}
                  />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontSize: "13px",
                        color: "text.secondary",
                        fontWeight: 500,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {file.fileName}
                    </Typography>
                    <Typography
                      sx={{ fontSize: "11px", color: "text.disabled" }}
                    >
                      {file.fileType?.toUpperCase()} ·{" "}
                      {formatFileSize(file.fileSize)}
                    </Typography>
                  </Box>
                  <IconButton
                    size="small"
                    onClick={() => handleFileDelete(file.fileId)}
                    sx={{
                      color: "text.disabled",
                      "&:hover": { color: "error.main" },
                    }}
                  >
                    <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Box>
              ))}
            </Box>
          )}
        </Box>

        <Divider sx={{ borderColor: "divider", my: 2.5 }} />

        <Box sx={{ mb: 2.5 }}>
          <Typography className="org-field-label">Connect Table</Typography>
          <Autocomplete
            multiple
            options={selectableKnowledgeTableOptions}
            getOptionLabel={(option) =>
              option.tableName || option.databaseId || ""
            }
            value={selectedKnowledgeTableValues}
            isOptionEqualToValue={(option, value) =>
              option.databaseId === value.databaseId
            }
            onChange={(_, newValue) => {
              setKnowledgeTables(normalizeKnowledgeTables(newValue));
            }}
            loading={loadingDatabases}
            renderInput={(params) => (
              <TextField
                {...params}
                className="org-styled-textfield"
                variant="outlined"
                placeholder={
                  normalizedKnowledgeTables.length === 0
                    ? "Select tables..."
                    : ""
                }
                size="small"
              />
            )}
            renderOption={(props, option) => (
              <li {...props} key={option.databaseId}>
                {option.tableName || option.databaseId}
              </li>
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  {...getTagProps({ index })}
                  key={option.databaseId}
                  label={option.tableName || option.databaseId}
                  size="small"
                  sx={{ fontSize: "12px", borderRadius: "4px" }}
                />
              ))
            }
            size="small"
          />
          <Typography
            sx={{ fontSize: "11px", color: "text.disabled", mt: 0.5 }}
          >
            Table data will be included as CSV in the knowledge context.
          </Typography>
        </Box>

        <Divider sx={{ borderColor: "divider", my: 2.5 }} />

        <Box sx={{ mb: 2.5 }}>
          <Typography className="org-field-label">Manual Context</Typography>
          <TipTapEditor
            value={knowledgeManualContext}
            onChange={setKnowledgeManualContext}
            placeholder="Add additional context, instructions, or reference material..."
            markdown
            showToolbar={true}
            minHeight="120px"
          />
          <Typography
            sx={{ fontSize: "11px", color: "text.disabled", mt: 0.5 }}
          >
            Markdown content that will be included in the agent's knowledge
            base.
          </Typography>
        </Box>
      </>
    );
  };

  const {
    isEditLoading,
    headerTitle,
    headerSubtitle,
    saveButtonDisabled,
    saveButtonLabel,
    avatarName,
  } = buildAgentConfigChromeState({
    isCreateMode,
    loaded,
    showCreationPathChooser,
    isAiCreatePath,
    name,
    saving,
    saveResult,
    normalizedAiRequirements,
    creationStage,
  });

  return (
    <AgentConfigDrawerShell
      open={open}
      onClose={onClose}
      saving={saving}
      isCreateMode={isCreateMode}
      loaded={loaded}
      showCreationPathChooser={showCreationPathChooser}
      showManualConfigurationLayout={showManualConfigurationLayout}
      isEditLoading={isEditLoading}
      headerTitle={headerTitle}
      headerSubtitle={headerSubtitle}
      avatarName={avatarName}
      isSystemAgent={isSystemAgent}
      saveErrorMessage={saveErrorMessage}
      handleSave={handleSave}
      saveButtonDisabled={saveButtonDisabled}
      saveResult={saveResult}
      saveButtonLabel={saveButtonLabel}
      renderCreationPathChooser={renderCreationPathChooser}
      resetCreationPathSelection={resetCreationPathSelection}
      activeTab={activeTab}
      setActiveTab={setActiveTab}
      navTabs={NAV_TABS}
      renderGeneralTab={renderGeneralTab}
      renderAccessTab={renderAccessTab}
      renderChatUxTab={renderChatUxTab}
      renderToolsTab={renderToolsTab}
      renderKnowledgeTab={renderKnowledgeTab}
      renderEmailTab={renderEmailTab}
      renderDangerTab={renderDangerTab}
      renderAiCreationPanel={renderAiCreationPanel}
    />
  );
}
